import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, Zap, Droplets, Palette, Play, Trophy, Home, X } from "lucide-react";
import { applyPageMetadata } from "../../lib/seo";
import {
  treeCoversForLevel,
  fallingBranchConfigForLevel,
  windConfigForLevel,
  slingshotPositionForLevel,
  uniqueObstaclesForLevel,
  LEVEL_POSITIONS,
  type TreeCover,
  type MonkeyBehavior,
  type WindDirection,
  type UniqueObstacleKind,
} from "./monkeyLevels";
import {
  SLINGSHOT_SKINS,
  type SlingshotSkinId,
  type SkillType,
  getStoredSkillAmmo,
  setStoredSkillAmmo,
  isSkinUnlocked,
  setSkinUnlocked,
  getActiveSkin,
  setActiveSkin,
  getHighScore,
  setHighScore,
  getMaxUnlockedLevel,
  setMaxUnlockedLevel,
  showTikTokRewardedVideoAd,
} from "./adManager";
import {
  startTembakMonyetBgm,
  stopTembakMonyetBgm,
  setTembakMonyetBgmEnabled,
  setTembakMonyetBgmMode,
} from "./tembakMonyetBgm";
import "./tembak-monyetnya.css";

const W = 420;
const H = 740;
const MAX_PULL = 100;

function timeLimitForLevel(level: number): number {
  const isBossLevel = level === 10 || level === 20;
  // Base time: Stage 1–10 = 15 detik, Stage 11–20 = 20 detik
  const baseTime = level >= 11 ? 20 : 15;
  // Boss Fight: base time + 5 detik (Level 10: 20s, Level 20: 25s)
  return isBossLevel ? baseTime + 5 : baseTime;
}

type PeekPhase = "hidden" | "warning" | "peeking" | "exposed" | "retreating";

type Monkey = {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  phase: number;
  hit: boolean;
  isBoss: boolean;
  hp: number;
  maxHp: number;
  hitFlash: number;
  behavior: MonkeyBehavior;
  patrolDistance: number;
  patrolSpeed: number;
  facingLeft: boolean;
  walkStep: number;
  hideSide: "left" | "right";
  peekPhase: PeekPhase;
  coverX: number;
  hasHelmet: boolean;
  helmetBroken: boolean;
  hasIronArmor: boolean;
  ironArmorBroken: boolean;
  canDodge: boolean;
  dodgeTime: number;
  laughTimer: number;
};

type TumblingMonkey = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vRot: number;
  isBoss: boolean;
  life: number;
};

type FallingBranch = {
  id: number;
  x: number;
  y: number;
  length: number;
  radius: number;
  angle: number;
  vy: number;
  vRot: number;
  wobble: number;
};

function getBranchSegment(b: FallingBranch) {
  const halfL = b.length / 2;
  const cosA = Math.cos(b.angle);
  const sinA = Math.sin(b.angle);
  return {
    x1: b.x - cosA * halfL,
    y1: b.y - sinA * halfL,
    x2: b.x + cosA * halfL,
    y2: b.y + sinA * halfL,
    radius: b.radius,
    wobble: b.wobble,
  };
}

type DriftingLeaf = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vRot: number;
  size: number;
  color: string;
};

type ProjectileKind = "normal" | "super" | "bomb";

type Ball = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  trail: { x: number; y: number }[];
  bounces: number;
  kind: ProjectileKind;
  targetX?: number;
  targetY?: number;
};
type Particle = { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: string; size: number };
type Impact = { x: number; y: number; text: string; subtext?: string; color: string; life: number };
type WindStreak = { x: number; y: number; len: number; speed: number; alpha: number; width: number };

type UniqueObstacle = {
  id: string;
  kind: UniqueObstacleKind;
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  radius: number;
  phase: number;
  hitFlash: number;
  wobble: number;
  hp: number;
  maxHp: number;
  broken: boolean;
  anchorX: number;
  anchorY: number;
};

type Scene = {
  sling: { x: number; y: number };
  monkeys: Monkey[];
  trees: TreeCover[];
  fallingBranches: FallingBranch[];
  uniqueObstacles: UniqueObstacle[];
  ball: Ball | null;
  aim: { x: number; y: number } | null;
  shots: number;
  time: number;
  timeLeft: number;
  particles: Particle[];
  impact: Impact | null;
  streak: number;
  clearDelay: number;
  recoil: number;
  screenShake: number;
  tumblingMonkeys: TumblingMonkey[];
  hitStop: number;
  wind: number; // legacy alias
  windX: number;
  windY: number;
  windTargetX: number;
  windTargetY: number;
  windTimer: number;
  windWaveActive: boolean;
  windWaveDuration: number;
  windWaveType: WindDirection | "calm";
  windBannerText: string;
  windBannerTimer: number;
  windStreaks: WindStreak[];
  bulletTime: number;
  driftingLeaves: DriftingLeaf[];
  activeSkill: SkillType | null;
  skinId: SlingshotSkinId;
};

type GameArt = {
  monkeyFront: HTMLImageElement | null;
  monkeyBack: HTMLImageElement | null;
  monkeyHit: HTMLImageElement | null;
  monkeyBoss: HTMLImageElement | null;
  slingshot: HTMLImageElement | null;
  branch: HTMLImageElement | null;
  pellet: HTMLImageElement | null;
  waterBalloon: HTMLImageElement | null;
};

const SPRITE_FILES: Record<keyof GameArt, string> = {
  monkeyFront: "monkey-front.png",
  monkeyBack: "monkey-back.png",
  monkeyHit: "monkey-hit.png",
  monkeyBoss: "monkey-boss.png",
  slingshot: "slingshot.png",
  branch: "branch.png",
  pellet: "pellet.png",
  waterBalloon: "water-balloon.png",
};

function makeScene(level: number, skinId: SlingshotSkinId = "classic"): Scene {
  const places = LEVEL_POSITIONS[level - 1];
  const trees = treeCoversForLevel(level);
  const branchCfg = fallingBranchConfigForLevel(level);
  const fallingBranches: FallingBranch[] = [];

  for (let i = 0; i < branchCfg.count; i++) {
    const len = branchCfg.minLength + Math.random() * (branchCfg.maxLength - branchCfg.minLength || 1);
    const vy = branchCfg.minSpeed + Math.random() * (branchCfg.maxSpeed - branchCfg.minSpeed || 1);
    // Stagger initial Y coordinates down the screen
    const startY = -40 + (i / (branchCfg.count || 1)) * 500;
    const x = 90 + Math.random() * (W - 180);
    const angle = ((Math.random() - 0.5) * 45 * Math.PI) / 180;
    const vRot = (Math.random() - 0.5) * 0.35;
    fallingBranches.push({
      id: i + 1,
      x,
      y: startY,
      length: len,
      radius: 11,
      angle,
      vy,
      vRot,
      wobble: 0,
    });
  }

  const driftingLeaves: DriftingLeaf[] = [];
  const isVolcano = level >= 11;
  for (let i = 0; i < 9; i++) {
    driftingLeaves.push({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 15,
      vy: isVolcano ? -(18 + Math.random() * 32) : 18 + Math.random() * 32,
      rot: Math.random() * Math.PI * 2,
      vRot: (Math.random() - 0.5) * 2.2,
      size: isVolcano ? 3 + Math.random() * 3.5 : 4 + Math.random() * 4.5,
      color: isVolcano
        ? i % 3 === 0
          ? "rgba(255, 69, 0, 0.65)"
          : i % 3 === 1
          ? "rgba(255, 186, 8, 0.75)"
          : "rgba(255, 238, 221, 0.55)"
        : i % 2 === 0
        ? "rgba(90, 166, 72, 0.45)"
        : "rgba(180, 215, 110, 0.4)",
    });
  }

  const monkeys: Monkey[] = places.map((p, i) => {
    const isBoss = Boolean(p.isBoss);
    const hp = p.hp ?? 1;
    const behavior: MonkeyBehavior = p.behavior ?? (level >= 6 ? "patrol" : "idle");
    const patrolDistance = p.patrolDistance ?? (isBoss ? 55 : 42);
    const patrolSpeed = p.patrolSpeed ?? 42;
    const hideSide = p.hideSide ?? (p.x < W / 2 ? "left" : "right");
    // CoverX sits tucked inside the thick tree trunk (left trunk x=32, right trunk x=388)
    const coverX = hideSide === "left" ? -12 : 432;
    const hasHelmet = Boolean(p.hasHelmet);
    const hasIronArmor = Boolean(p.hasIronArmor);
    const canDodge = Boolean(p.canDodge);

    return {
      x: p.x,
      y: p.y,
      baseX: p.x,
      baseY: p.y,
      phase: i * 2.1 + level,
      hit: false,
      isBoss,
      hp,
      maxHp: hp,
      hitFlash: 0,
      behavior,
      patrolDistance,
      patrolSpeed,
      facingLeft: hideSide === "right",
      walkStep: 0,
      hideSide,
      peekPhase: "exposed",
      coverX,
      hasHelmet,
      helmetBroken: false,
      hasIronArmor,
      ironArmorBroken: false,
      canDodge,
      dodgeTime: 0,
      laughTimer: 0,
    };
  });
  const totalHp = monkeys.reduce((sum, m) => sum + m.hp, 0);
  const shots = Math.max(5, totalHp * 2 + (level >= 8 ? 2 : 1));

  const sling = slingshotPositionForLevel(level);
  const obstacleDefs = uniqueObstaclesForLevel(level);
  const uniqueObstacles: UniqueObstacle[] = obstacleDefs.map((def, idx) => ({
    id: `uo-${level}-${idx}-${def.kind}`,
    kind: def.kind,
    x: def.x,
    y: def.y,
    baseX: def.x,
    baseY: def.y,
    radius: def.kind === "bumper_crystal" ? 20 : def.kind === "beehive" ? 22 : 18,
    phase: idx * 1.7 + level,
    hitFlash: 0,
    wobble: 0,
    hp: def.hp ?? (def.kind === "beehive" ? 2 : 1),
    maxHp: def.hp ?? (def.kind === "beehive" ? 2 : 1),
    broken: false,
    anchorX: def.anchorX ?? def.x,
    anchorY: def.anchorY ?? (def.y - 120),
  }));

  const windStreaks: WindStreak[] = Array.from({ length: 22 }, () => ({
    x: Math.random() * W,
    y: Math.random() * H,
    len: 26 + Math.random() * 45,
    speed: 130 + Math.random() * 160,
    alpha: 0.25 + Math.random() * 0.45,
    width: 1.2 + Math.random() * 1.5,
  }));

  return {
    sling,
    monkeys,
    trees,
    fallingBranches,
    uniqueObstacles,
    ball: null,
    aim: null,
    shots,
    time: 0,
    timeLeft: timeLimitForLevel(level),
    particles: [],
    impact: null,
    streak: 0,
    clearDelay: 0,
    recoil: 0,
    screenShake: 0,
    tumblingMonkeys: [],
    hitStop: 0,
    wind: 0,
    windX: 0,
    windY: 0,
    windTargetX: 0,
    windTargetY: 0,
    windTimer: 0,
    windWaveActive: false,
    windWaveDuration: 0,
    windWaveType: "calm",
    windBannerText: "",
    windBannerTimer: 0,
    windStreaks,
    bulletTime: 1.0,
    driftingLeaves,
    activeSkill: null,
    skinId,
  };
}

function monkeyPosition(monkey: Monkey, time: number) {
  if (monkey.behavior === "idle") {
    return { x: monkey.baseX, y: monkey.baseY };
  }

  if (monkey.behavior === "patrol") {
    const spd = monkey.patrolSpeed / 25;
    const currentX = monkey.baseX + Math.sin(time * spd + monkey.phase) * monkey.patrolDistance;
    // Rhythmic stepping bob when walking
    const stepBob = Math.abs(Math.sin(time * 6.5 + monkey.phase)) * 4.5;
    return {
      x: currentX,
      y: monkey.baseY - stepBob,
    };
  }

  if (monkey.behavior === "peek") {
    const cyclePeriod = 5.2;
    const t = (time + monkey.phase * 1.5) % cyclePeriod;
    const dx = monkey.baseX - monkey.coverX;
    let exposeRatio = 1.0;

    if (t < 1.6) {
      // Hidden behind tree
      exposeRatio = 0.0;
    } else if (t < 2.3) {
      // Warning tell (eyes peeking right at tree edge)
      exposeRatio = 0.28;
    } else if (t < 2.7) {
      // Peeking slide out
      const prog = (t - 2.3) / 0.4;
      exposeRatio = 0.28 + 0.72 * Math.min(1, Math.max(0, prog));
    } else if (t < 4.6) {
      // Fully exposed, taunting & vulnerable
      exposeRatio = 1.0;
    } else {
      // Retreating back into cover
      const prog = (t - 4.6) / 0.6;
      exposeRatio = 1.0 - Math.min(1, Math.max(0, prog));
    }

    const naturalBob = Math.sin(time * 3.5 + monkey.phase) * 2;
    return {
      x: monkey.coverX + dx * exposeRatio,
      y: monkey.baseY + naturalBob,
    };
  }

  return { x: monkey.baseX, y: monkey.baseY };
}

function segmentDistance(x: number, y: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(x - x1 - t * dx, y - y1 - t * dy);
}

function roundedLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, width: number, color: string) {
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
  ctx.lineCap = "round"; ctx.lineWidth = width; ctx.strokeStyle = color; ctx.stroke();
}

function drawFallingBranches(
  ctx: CanvasRenderingContext2D,
  branches: FallingBranch[],
  art: GameArt,
  time: number,
  isVolcano: boolean = false
) {
  for (const fb of branches) {
    ctx.save();
    ctx.translate(fb.x, fb.y);
    const wobbleAngle = fb.wobble > 0 ? Math.sin(time * 28) * 0.1 * fb.wobble : 0;
    ctx.rotate(fb.angle + wobbleAngle);

    if (isVolcano) {
      // Jagged volcanic stalactite / magma rock
      const halfL = fb.length / 2;
      const r = fb.radius;
      ctx.fillStyle = "#1e1412";
      ctx.beginPath();
      ctx.moveTo(-halfL, 0);
      ctx.lineTo(-halfL * 0.6, -r * 1.3);
      ctx.lineTo(0, -r * 0.8);
      ctx.lineTo(halfL * 0.7, -r * 1.2);
      ctx.lineTo(halfL, 0);
      ctx.lineTo(halfL * 0.5, r * 1.1);
      ctx.lineTo(-halfL * 0.2, r * 1.4);
      ctx.closePath();
      ctx.fill();

      // Sharp basalt facet highlight
      ctx.strokeStyle = "#432822";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Glowing magma crack along stalactite
      ctx.strokeStyle = "#ff4500";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(-halfL + 8, 0);
      ctx.lineTo(-halfL * 0.2, -r * 0.3);
      ctx.lineTo(halfL * 0.4, r * 0.3);
      ctx.lineTo(halfL - 8, 0);
      ctx.stroke();

      // Hot core vein
      ctx.strokeStyle = "#ffea00";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-halfL * 0.5, -r * 0.1);
      ctx.lineTo(halfL * 0.3, r * 0.1);
      ctx.stroke();

      // Magma embers sparks around stalactite
      for (let ei = 0; ei < 2; ei++) {
        const ex = -halfL * 0.4 + ei * halfL * 0.8 + Math.sin(time * 5 + fb.id) * 4;
        const ey = -r - 4 + Math.cos(time * 6 + ei) * 3;
        ctx.fillStyle = "#ff6a00";
        ctx.beginPath();
        ctx.arc(ex, ey, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (art.branch) {
      const bh = fb.radius * 4.2;
      ctx.drawImage(art.branch, -fb.length / 2 - 12, -bh / 2, fb.length + 24, bh);
    } else {
      roundedLine(ctx, -fb.length / 2, 0, fb.length / 2, 0, fb.radius * 2, "#3a301c");
      roundedLine(ctx, -fb.length / 2, -3, fb.length / 2, -3, 4, "#a98a47");
    }

    if (!isVolcano) {
      // Fluttering jungle leaves on the falling branch
      const leafCount = 3;
      for (let li = 0; li < leafCount; li++) {
        const lx = -fb.length / 2 + (li + 0.5) * (fb.length / leafCount);
        const ly = -fb.radius - 2;
        const leafSway = Math.sin(time * 4.2 + li * 2 + fb.id) * 3.2;
        ctx.fillStyle = li % 2 === 0 ? "#337633" : "#4fa243";
        ctx.beginPath();
        ctx.ellipse(lx + leafSway, ly, 6, 11, 0.3 + leafSway * 0.05, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }
}

function drawMonkey(
  ctx: CanvasRenderingContext2D,
  m: Monkey,
  x: number,
  y: number,
  time: number,
  art: GameArt,
  isNervous: boolean
) {
  if (m.hit) return;
  // If completely hidden behind tree trunk, don't draw in open view
  if (m.behavior === "peek" && m.peekPhase === "hidden") return;

  ctx.save();

  // Hit flash offset
  const flashOffset = m.hitFlash > 0 ? (Math.random() - 0.5) * 6 : 0;

  // Agile dodging duck offset
  const dodgeOffset = m.dodgeTime > 0 ? Math.sin((m.dodgeTime / 0.45) * Math.PI) * 22 : 0;

  // Laughing belly shake & cheeky wiggle on player's miss
  const laughShakeX = m.laughTimer > 0 ? Math.sin(time * 32) * 5 : 0;
  const laughScaleX = m.laughTimer > 0 ? 1.14 - Math.abs(Math.sin(time * 20)) * 0.12 : 1;
  const laughScaleY = m.laughTimer > 0 ? 0.86 + Math.abs(Math.sin(time * 20)) * 0.18 : 1;

  // Natural breathing bob & lively cheek-pull micro pulse
  const bobY = Math.sin(time * 3.6 + m.phase) * 2.8;
  const cheekPulseX = 1 + 0.028 * Math.sin(time * 5.2 + m.phase);
  const cheekPulseY = 1 - 0.018 * Math.sin(time * 5.2 + m.phase);

  // Walking tilt: leans slightly forward in walking direction
  const walkTilt = m.behavior === "patrol" ? (m.facingLeft ? -0.06 : 0.06) : 0;

  const isBoss = m.isBoss;
  // Non-boss monkeys will occasionally turn around to tease (show back & wiggle taunt)
  const isBack = !isBoss && Math.sin(time * 1.5 + m.phase) > 0.68;
  const backWiggle = isBack ? Math.sin(time * 14) * 0.08 : 0;
  const backBounce = isBack ? Math.abs(Math.sin(time * 14)) * 3.5 : 0;

  ctx.translate(x + flashOffset + laughShakeX, y + bobY + dodgeOffset - backBounce);
  ctx.rotate(walkTilt + backWiggle);

  // Facing direction: flip horizontally if facing left
  const dirScale = m.facingLeft ? -1 : 1;
  ctx.scale(dirScale * cheekPulseX * laughScaleX, cheekPulseY * laughScaleY);

  const w = isBoss ? 165 : 130;
  const h = isBoss ? 165 : 130;
  const offX = -w / 2;
  const offY = -h / 2 - (isBoss ? 12 : 8);

  if (m.hitFlash > 0) {
    ctx.filter = "brightness(1.9) drop-shadow(0 0 12px #ff3b30)";
  }

  if (isBoss) {
    if (art.monkeyBoss) {
      ctx.drawImage(art.monkeyBoss, offX, offY, w, h);
    }
  } else if (isBack) {
    if (art.monkeyBack) {
      ctx.drawImage(art.monkeyBack, offX, offY, w, h);
    }
  } else {
    if (art.monkeyFront) {
      ctx.drawImage(art.monkeyFront, offX, offY, w, h);
    }
  }

  ctx.filter = "none";

  // Coconut Shell Helmet ("Helm Batok Kelapa")
  if (m.hasHelmet && !m.helmetBroken) {
    ctx.save();
    const helmetY = offY + (isBoss ? 20 : 16);
    // Outer coconut husk shell
    ctx.fillStyle = "#4a2a12";
    ctx.beginPath();
    ctx.arc(0, helmetY, isBoss ? 42 : 34, Math.PI, 0);
    ctx.closePath();
    ctx.fill();

    // Coconut husk rim / band
    ctx.fillStyle = "#70401d";
    ctx.beginPath();
    ctx.roundRect(-(isBoss ? 44 : 36), helmetY - 2, isBoss ? 88 : 72, 7, 3);
    ctx.fill();

    // Coconut fiber ridges
    ctx.strokeStyle = "#301808";
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(0, helmetY, isBoss ? 35 : 28, Math.PI * 1.15, Math.PI * 1.85);
    ctx.stroke();

    // Palm leaf sprout on top of helmet
    ctx.fillStyle = "#4fa243";
    ctx.beginPath();
    ctx.ellipse(3, helmetY - (isBoss ? 44 : 36), 4, 11, 0.4, 0, Math.PI * 2);
    ctx.ellipse(-3, helmetY - (isBoss ? 44 : 36), 4, 11, -0.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // Iron Armor ("Zirah Besi")
  if (m.hasIronArmor && !m.ironArmorBroken) {
    ctx.save();
    const helmetY = offY + (isBoss ? 20 : 16);
    // Outer steel helmet shell with metallic gradient
    const grad = ctx.createLinearGradient(-35, helmetY - 35, 35, helmetY);
    grad.addColorStop(0, "#4a5568");
    grad.addColorStop(0.35, "#e2e8f0");
    grad.addColorStop(0.7, "#718096");
    grad.addColorStop(1, "#2d3748");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, helmetY, isBoss ? 42 : 34, Math.PI, 0);
    ctx.closePath();
    ctx.fill();

    // Riveted iron brow band
    ctx.fillStyle = "#1a202c";
    ctx.beginPath();
    ctx.roundRect(-(isBoss ? 44 : 36), helmetY - 2, isBoss ? 88 : 72, 7, 2);
    ctx.fill();

    // Golden brass rivets
    ctx.fillStyle = "#ecc94b";
    const rivetCount = isBoss ? 6 : 5;
    const rSpan = isBoss ? 36 : 28;
    for (let ri = 0; ri < rivetCount; ri++) {
      const rx = -rSpan + (ri / (rivetCount - 1)) * (rSpan * 2);
      ctx.beginPath();
      ctx.arc(rx, helmetY + 1.5, 1.8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Steel horned crest on top of helmet
    ctx.fillStyle = "#cbd5e0";
    ctx.beginPath();
    ctx.moveTo(-6, helmetY - (isBoss ? 42 : 34));
    ctx.lineTo(0, helmetY - (isBoss ? 56 : 46));
    ctx.lineTo(6, helmetY - (isBoss ? 42 : 34));
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  // Boss HP Hearts
  if (isBoss && m.maxHp > 1) {
    const heartSpacing = 18;
    const startX = -(m.maxHp - 1) * heartSpacing / 2;
    const heartY = offY - 14;
    for (let hIdx = 0; hIdx < m.maxHp; hIdx++) {
      const active = hIdx < m.hp;
      ctx.fillStyle = active ? "#ff3355" : "#443333";
      ctx.beginPath();
      const hx = startX + hIdx * heartSpacing;
      ctx.arc(hx - 4, heartY, 4.5, Math.PI, 0);
      ctx.arc(hx + 4, heartY, 4.5, Math.PI, 0);
      ctx.lineTo(hx + 8, heartY);
      ctx.lineTo(hx, heartY + 9);
      ctx.lineTo(hx - 8, heartY);
      ctx.closePath();
      ctx.fill();
    }
  }

  // Warning Tell: Peeking eyes when about to pop out of tree
  if (m.behavior === "peek" && m.peekPhase === "warning") {
    ctx.font = "bold 22px system-ui";
    ctx.fillText("👀", -11, offY - 6);
  }

  // Nervous sweat drop when player aims near monkey!
  if (isNervous) {
    ctx.font = "bold 24px system-ui";
    ctx.fillText("💦", offX + w - 12, offY + 20);
  }

  ctx.restore();
}

function drawMonkeyChatBubble(
  ctx: CanvasRenderingContext2D,
  m: Monkey,
  x: number,
  y: number,
  time: number,
  isNervous: boolean
) {
  if (m.hit) return;
  // If hidden behind tree trunk or giving warning tell, don't show chat
  if (m.behavior === "peek" && (m.peekPhase === "hidden" || m.peekPhase === "warning")) return;

  // Stagger chat cycles so monkeys take turns taunting with a calm, readable pace
  const cyclePeriod = 7.5;
  const cycleTime = (time + m.phase * 2.5) % cyclePeriod;
  const isTaunting = cycleTime < 2.4;
  const isLaughing = m.laughTimer > 0;
  const isDodging = m.dodgeTime > 0.08;

  // Show if monkey is taunting, laughing at miss, reacting with nervous panic, or dodging
  const showChat = isNervous || isTaunting || isLaughing || isDodging;
  if (!showChat) return;

  let taunt: string;
  let bubbleBg = "#fffbe8"; // Warm vibrant cream
  let textColor = "#4a2a10"; // Deep jungle bark brown
  let borderColor = "#42240c";

  // Cycle counter integer to guarantee stable text during the entire speech bubble window
  const cycleCount = Math.floor((time + m.phase * 2.5) / cyclePeriod);

  if (isLaughing) {
    const missLines = [
      "WLEEE GA KENAA! 😜",
      "HAMPIRR BANGET! 🤭",
      "WLEEE COBA LAGI! 😝",
      "YAAA MELESET! 🍃",
      "AYOO SEMANGAT! 🌟",
      "DIKIT LAGI LHO! 🎯",
    ];
    if (m.isBoss) {
      taunt = "HOHO COBA LAGI! 👑";
      bubbleBg = "#fff7ed";
      textColor = "#431407";
      borderColor = "#542008";
    } else {
      // Pick one line based on monkey phase and keep it completely stable for the whole laugh
      const idx = Math.floor(Math.abs(m.phase * 13 + m.baseX)) % missLines.length;
      taunt = missLines[idx];
      bubbleBg = "#fffde6";
      textColor = "#b71c1c";
      borderColor = "#7f1d1d";
    }
  } else if (isDodging) {
    taunt = "HUP! 💨";
    bubbleBg = "#e8f5e9";
    textColor = "#1b5e20";
    borderColor = "#2e7d32";
  } else if (m.hasIronArmor && m.ironArmorBroken && m.hitFlash > 0.05) {
    taunt = "ZIRAHKU PECAH! 🛡️";
    bubbleBg = "#f1f5f9";
    textColor = "#334155";
    borderColor = "#1e293b";
  } else if (m.hasHelmet && m.helmetBroken && m.hitFlash > 0.05) {
    taunt = "HELMKU PECAH! 🥥";
    bubbleBg = "#fff3e0";
    textColor = "#d84315";
    borderColor = "#bf360c";
  } else if (isNervous) {
    taunt = m.isBoss ? "JANGAN BIDIK! 💢" : Math.floor(m.phase * 7) % 2 === 0 ? "WADUH! 😱" : "UPS! 💦";
    bubbleBg = "#fff3e0";
    textColor = "#b71c1c";
    borderColor = "#801515";
  } else if (m.isBoss) {
    const bossLines = ["GRRR! 👑", "AKU RAJA RIMBA! 👑", "BELUM KENA NIH! 🌴", "COBA LAGI! ✨"];
    const idx = (cycleCount + Math.floor(m.phase * 5)) % bossLines.length;
    taunt = bossLines[idx];
    bubbleBg = "#fff7ed";
    textColor = "#431407";
    borderColor = "#542008";
  } else if (m.behavior === "peek") {
    const peekLines = ["CILUKBA! 🙈", "WLEEE! 😜", "NGUMPET LAGI~ 🍃", "GA KENAA! 😝"];
    const idx = (cycleCount + Math.floor(m.phase * 5)) % peekLines.length;
    taunt = peekLines[idx];
  } else if (m.behavior === "patrol") {
    const patrolLines = ["LARI DULU YA~ 🏃", "WLEEE! 😝", "GESIT BANGET KAN? 💨", "GA KENAA! 🐒"];
    const idx = (cycleCount + Math.floor(m.phase * 5)) % patrolLines.length;
    taunt = patrolLines[idx];
  } else {
    const idleLines = ["WLEEE! 😛", "SINI COBA! 🎯", "DIKIT LAGI KOK! 🤭", "GA KENAA! 😝"];
    const idx = (cycleCount + Math.floor(m.phase * 5)) % idleLines.length;
    taunt = idleLines[idx];
  }

  ctx.save();
  ctx.font = "900 13px system-ui, -apple-system, sans-serif";
  const textWidth = ctx.measureText(taunt).width;
  const bw = Math.max(74, Math.round(textWidth + 24));
  const bh = 28;
  const isBoss = m.isBoss;

  // Vertical clearance:
  // Normal monkey head reaches y - 73 -> clearance 78px
  // Boss head reaches y - 95 and HP hearts sit at y - 108.5 (top at y - 114) -> clearance 122px
  const clearance = isBoss ? 122 : 78;
  let by = y - clearance - bh;
  // Guard against top HUD overlap (HUD buttons & score at y <= 42)
  if (by < 46) {
    by = 46;
  }

  // Horizontal position:
  // Center directly over monkey, with slight bias toward the direction it's facing
  const facingBias = m.facingLeft ? -10 : 10;
  let bx = x + facingBias - bw / 2;
  // Clamp within canvas boundaries
  bx = Math.max(12, Math.min(W - bw - 12, bx));

  // Speech bubble pointer / tail coordinates pointing to monkey's head
  const tailBaseX = Math.max(bx + 16, Math.min(bx + bw - 16, x + facingBias));
  const tailBaseY = by + bh;
  const tipX = x + (m.facingLeft ? -8 : 8);
  const tipY = Math.max(tailBaseY + 6, isBoss ? y - 114 : y - 72);

  // Subtle comic drop shadow
  ctx.shadowColor = "rgba(0, 0, 0, 0.22)";
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 2;

  // 1. Draw bubble fill
  ctx.beginPath();
  ctx.roundRect(bx, by, bw, bh, 10);
  ctx.fillStyle = bubbleBg;
  ctx.fill();

  // 2. Draw tail fill
  ctx.beginPath();
  ctx.moveTo(tailBaseX - 6, tailBaseY - 1);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(tailBaseX + 6, tailBaseY - 1);
  ctx.closePath();
  ctx.fill();

  // Turn off shadow for crisp borders & text
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // 3. Draw border outline
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw tail stroke
  ctx.beginPath();
  ctx.moveTo(tailBaseX - 6, tailBaseY);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(tailBaseX + 6, tailBaseY);
  ctx.stroke();

  // Mask inner tail seam
  ctx.fillStyle = bubbleBg;
  ctx.fillRect(tailBaseX - 5, tailBaseY - 2, 10, 3);

  // 4. Centered crisp text
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(taunt, bx + bw / 2, by + bh / 2 + 1);

  ctx.restore();
}

function drawTreeCovers(ctx: CanvasRenderingContext2D, trees: TreeCover[], time: number, isVolcano: boolean = false) {
  for (const tree of trees) {
    ctx.save();
    const x = tree.x;
    const w = tree.width;
    const halfW = w / 2;

    if (isVolcano) {
      // 1. Basalt volcanic stone pillar body
      const pillarGrad = ctx.createLinearGradient(x - halfW, 0, x + halfW, 0);
      if (tree.side === "left") {
        pillarGrad.addColorStop(0, "#120b0a");
        pillarGrad.addColorStop(0.35, "#241816");
        pillarGrad.addColorStop(0.75, "#3a2824");
        pillarGrad.addColorStop(1, "#1c110f");
      } else {
        pillarGrad.addColorStop(0, "#1c110f");
        pillarGrad.addColorStop(0.25, "#3a2824");
        pillarGrad.addColorStop(0.65, "#241816");
        pillarGrad.addColorStop(1, "#120b0a");
      }
      ctx.fillStyle = pillarGrad;
      ctx.beginPath();
      ctx.roundRect(x - halfW, tree.y1, w, tree.y2 - tree.y1, [10, 10, 12, 12]);
      ctx.fill();

      // 2. Basalt rock fractures
      ctx.strokeStyle = "rgba(10, 5, 5, 0.7)";
      ctx.lineWidth = 2.5;
      for (let by = tree.y1 + 35; by < tree.y2 - 20; by += 52) {
        ctx.beginPath();
        ctx.moveTo(x - halfW + 4, by);
        ctx.lineTo(x + 2, by + 12);
        ctx.lineTo(x + halfW - 4, by + 4);
        ctx.stroke();
      }

      // 3. Glowing molten magma veins
      ctx.strokeStyle = "rgba(255, 69, 0, 0.75)";
      ctx.lineWidth = 2.2;
      for (let vy = tree.y1 + 45; vy < tree.y2 - 30; vy += 65) {
        const vx = tree.side === "left" ? x + halfW - 10 : x - halfW + 10;
        ctx.beginPath();
        ctx.moveTo(vx, vy);
        ctx.lineTo(vx + (tree.side === "left" ? 6 : -6), vy + 20);
        ctx.lineTo(vx + (tree.side === "left" ? 1 : -1), vy + 42);
        ctx.stroke();
      }

      // 4. Hot magma glowing ember nodes
      ctx.fillStyle = "#ffbb00";
      for (let ey = tree.y1 + 55; ey < tree.y2 - 40; ey += 65) {
        const ex = tree.side === "left" ? x + halfW - 6 : x - halfW + 6;
        ctx.beginPath();
        ctx.arc(ex, ey, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }

      // 5. Obsidian rock outcrops
      const rockDark = "#1c1412";
      const rockMid = "#35221e";
      const clusterX = tree.side === "left" ? x + halfW : x - halfW;
      for (let ly = tree.y1 + 45; ly < tree.y2; ly += 130) {
        ctx.fillStyle = rockDark;
        ctx.beginPath();
        ctx.arc(clusterX + (tree.side === "left" ? 4 : -4), ly, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = rockMid;
        ctx.beginPath();
        ctx.arc(clusterX + (tree.side === "left" ? 10 : -10), ly - 4, 9, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      // 1. Main tree trunk body with bark gradient
      const trunkGrad = ctx.createLinearGradient(x - halfW, 0, x + halfW, 0);
      if (tree.side === "left") {
        trunkGrad.addColorStop(0, "#2b180d");
        trunkGrad.addColorStop(0.35, "#442614");
        trunkGrad.addColorStop(0.75, "#5d351b");
        trunkGrad.addColorStop(1, "#361d0d");
      } else {
        trunkGrad.addColorStop(0, "#361d0d");
        trunkGrad.addColorStop(0.25, "#5d351b");
        trunkGrad.addColorStop(0.65, "#442614");
        trunkGrad.addColorStop(1, "#2b180d");
      }

      ctx.fillStyle = trunkGrad;
      ctx.beginPath();
      ctx.roundRect(x - halfW, tree.y1, w, tree.y2 - tree.y1, [14, 14, 16, 16]);
      ctx.fill();

      // 2. Bark ridges and wood grain
      ctx.strokeStyle = "rgba(22, 11, 5, 0.45)";
      ctx.lineWidth = 2.5;
      for (let by = tree.y1 + 35; by < tree.y2 - 20; by += 52) {
        ctx.beginPath();
        ctx.moveTo(x - halfW + 4, by);
        ctx.bezierCurveTo(x - 2, by + 10, x + 2, by - 8, x + halfW - 4, by + 6);
        ctx.stroke();
      }

      // 3. Moss and lichen patches
      ctx.fillStyle = "#3e6328";
      for (let my = tree.y1 + 65; my < tree.y2 - 50; my += 85) {
        ctx.beginPath();
        const mossX = tree.side === "left" ? x + halfW - 10 : x - halfW + 10;
        ctx.ellipse(mossX, my, 7, 20, tree.side === "left" ? 0.2 : -0.2, 0, Math.PI * 2);
        ctx.fill();
      }

      // 4. Hanging tropical liana vines
      ctx.strokeStyle = "#5a7a32";
      ctx.lineWidth = 2;
      ctx.beginPath();
      const vineX = tree.side === "left" ? x + halfW - 4 : x - halfW + 4;
      ctx.moveTo(vineX, tree.y1);
      for (let vy = tree.y1; vy < tree.y2; vy += 38) {
        const sway = Math.sin(time * 2.2 + vy * 0.05) * 3.5;
        ctx.lineTo(vineX + sway, vy + 38);
      }
      ctx.stroke();

      // 5. Leaf clusters at perches
      const leafGreen = "#275932";
      const leafBright = "#428042";
      const clusterX = tree.side === "left" ? x + halfW : x - halfW;

      for (let ly = tree.y1 + 45; ly < tree.y2; ly += 130) {
        const sway = Math.sin(time * 2.8 + ly) * 2;
        ctx.fillStyle = leafGreen;
        ctx.beginPath();
        ctx.arc(clusterX + (tree.side === "left" ? 5 : -5) + sway, ly, 18, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = leafBright;
        ctx.beginPath();
        ctx.arc(clusterX + (tree.side === "left" ? 12 : -12) + sway, ly - 4, 12, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }
}

function drawTumblingMonkeys(ctx: CanvasRenderingContext2D, s: Scene, art: GameArt) {
  for (const tm of s.tumblingMonkeys) {
    ctx.save();
    ctx.translate(tm.x, tm.y);
    ctx.rotate(tm.rot);
    const size = tm.isBoss ? 150 : 120;
    const off = -size / 2;
    ctx.globalAlpha = Math.max(0, Math.min(1, tm.life * 1.8));

    const img = tm.isBoss ? (art.monkeyBoss ?? art.monkeyHit) : art.monkeyHit;
    if (img) {
      ctx.drawImage(img, off, off, size, size);
    }

    // Orbiting dizzy cartoon stars
    const starDist = size * 0.42;
    for (let i = 0; i < 3; i++) {
      const starAngle = s.time * 7 + (i * Math.PI * 2) / 3;
      const sx = Math.cos(starAngle) * starDist;
      const sy = Math.sin(starAngle) * (starDist * 0.5) - 10;
      ctx.font = "bold 15px system-ui";
      ctx.fillText("⭐", sx - 7, sy + 6);
    }

    ctx.restore();
  }
}

function drawSlingshot(ctx: CanvasRenderingContext2D, s: Scene, art: GameArt, skinId: SlingshotSkinId, isVolcano = false) {
  const slingX = s.sling.x;
  const slingY = s.sling.y;
  const pouch = s.aim ?? s.sling;
  const pullDx = pouch.x - slingX;
  const pullDy = pouch.y - slingY;
  const pull = Math.hypot(pullDx, pullDy);
  const isPulling = Boolean(s.aim && pull > 10);
  const skin = SLINGSHOT_SKINS.find((sk) => sk.id === skinId) || SLINGSHOT_SKINS[0];

  ctx.save();
  ctx.translate(0, s.recoil * 5 * Math.sin(s.time * 25));

  // Scaled dimensions matching cropped slingshot.png (553x902)
  const SW = 74;
  const SH = 120;
  const SX = slingX - SW / 2;
  const SY = slingY - 4;

  // Exact tip coordinates
  const leftTip = { x: slingX - 28.5, y: slingY - 3 };
  const rightTip = { x: slingX + 28.5, y: slingY - 3 };
  const forkCenter = { x: slingX, y: slingY - 3 };

  // 1. GROUNDED POST & BASE (Anchors slingshot firmly into ground at slingX)
  // Base ground occlusion shadow
  ctx.fillStyle = isVolcano ? "rgba(20, 5, 2, 0.75)" : "rgba(8, 22, 14, 0.7)";
  ctx.beginPath();
  ctx.ellipse(slingX, 712, 38, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = isVolcano ? "rgba(10, 2, 0, 0.9)" : "rgba(4, 12, 8, 0.85)";
  ctx.beginPath();
  ctx.ellipse(slingX, 714, 24, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  // Sturdy carved wooden log / obsidian post under handle
  const postTop = SY + SH - 10;
  const postHeight = Math.max(16, 730 - postTop);
  const postHalfW = 13;
  const postGrad = ctx.createLinearGradient(slingX - postHalfW, 0, slingX + postHalfW, 0);
  if (isVolcano) {
    postGrad.addColorStop(0, "#180d08");
    postGrad.addColorStop(0.2, "#2e1810");
    postGrad.addColorStop(0.5, "#422316");
    postGrad.addColorStop(0.8, "#2e1810");
    postGrad.addColorStop(1, "#140905");
  } else {
    postGrad.addColorStop(0, "#2c1508");
    postGrad.addColorStop(0.2, "#4a2612");
    postGrad.addColorStop(0.5, "#68391b");
    postGrad.addColorStop(0.8, "#4a2612");
    postGrad.addColorStop(1, "#231006");
  }
  ctx.fillStyle = postGrad;
  ctx.beginPath();
  ctx.roundRect(slingX - postHalfW, postTop, postHalfW * 2, postHeight, [0, 0, 6, 6]);
  ctx.fill();
  ctx.strokeStyle = isVolcano ? "#2c1208" : "#1a0c04";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Wood grain / volcanic grooves on post
  ctx.strokeStyle = isVolcano ? "#ff5722" : "#381c0c";
  ctx.lineWidth = isVolcano ? 1.2 : 1.6;
  ctx.beginPath();
  ctx.moveTo(slingX - 5, postTop + 4);
  ctx.lineTo(slingX - 5, 730);
  ctx.moveTo(slingX + 5, postTop + 4);
  ctx.lineTo(slingX + 5, 730);
  ctx.stroke();

  if (isVolcano) {
    // Glowing lava vein cracks at base
    ctx.strokeStyle = "#ff9800";
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(slingX, postTop + 8);
    ctx.lineTo(slingX, 730);
    ctx.stroke();

    // Charred volcanic rock crumbs
    ctx.fillStyle = "#26110a";
    ctx.beginPath();
    ctx.ellipse(slingX, 708, 26, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ff5722";
    ctx.beginPath();
    ctx.ellipse(slingX, 702, 12, 4, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Bark highlight
    ctx.strokeStyle = "#7a4623";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(slingX, postTop + 8);
    ctx.lineTo(slingX, 730);
    ctx.stroke();

    // Moss and lush jungle foliage tufts at ground contact
    ctx.fillStyle = "#1e4c22";
    ctx.beginPath();
    ctx.ellipse(slingX, 706, 26, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#347432";
    ctx.beginPath();
    ctx.ellipse(slingX, 700, 18, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#4ea048";
    ctx.beginPath();
    ctx.ellipse(slingX, 696, 11, 4, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 2. SLINGSHOT WOODEN FRAME (Drawn behind bands and pouch, with skin customization)
  if (art.slingshot) {
    ctx.drawImage(art.slingshot, SX, SY, SW, SH);
    if (skinId === "gold") {
      ctx.save();
      ctx.globalCompositeOperation = "source-atop";
      const goldGrad = ctx.createLinearGradient(SX, SY, SX + SW, SY + SH);
      goldGrad.addColorStop(0, "rgba(255, 215, 0, 0.28)");
      goldGrad.addColorStop(0.5, "rgba(255, 255, 220, 0.45)");
      goldGrad.addColorStop(1, "rgba(218, 165, 32, 0.32)");
      ctx.fillStyle = goldGrad;
      ctx.fillRect(SX, SY, SW, SH);
      ctx.restore();
    } else if (skinId === "cyber") {
      ctx.save();
      ctx.globalCompositeOperation = "source-atop";
      ctx.fillStyle = "rgba(0, 30, 40, 0.35)";
      ctx.fillRect(SX, SY, SW, SH);
      ctx.restore();
      ctx.strokeStyle = "#00f0ff";
      ctx.lineWidth = 1.6;
      ctx.shadowColor = "#00f0ff";
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(SX + SW * 0.25, SY + SH * 0.16);
      ctx.lineTo(SX + SW * 0.5, SY + SH * 0.54);
      ctx.lineTo(SX + SW * 0.75, SY + SH * 0.16);
      ctx.moveTo(SX + SW * 0.5, SY + SH * 0.54);
      ctx.lineTo(SX + SW * 0.5, SY + SH * 0.86);
      ctx.stroke();
      ctx.shadowBlur = 0;
    } else if (skinId === "magma") {
      ctx.save();
      ctx.globalCompositeOperation = "source-atop";
      ctx.fillStyle = "rgba(40, 10, 5, 0.35)";
      ctx.fillRect(SX, SY, SW, SH);
      ctx.restore();
      ctx.strokeStyle = "#ff4500";
      ctx.lineWidth = 1.6;
      ctx.shadowColor = "#ff3b00";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.moveTo(SX + SW * 0.28, SY + SH * 0.22);
      ctx.lineTo(SX + SW * 0.42, SY + SH * 0.4);
      ctx.lineTo(SX + SW * 0.5, SY + SH * 0.6);
      ctx.lineTo(SX + SW * 0.47, SY + SH * 0.83);
      ctx.moveTo(SX + SW * 0.72, SY + SH * 0.24);
      ctx.lineTo(SX + SW * 0.58, SY + SH * 0.43);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  // 3. RUBBER BANDS (Drawn IN FRONT of the wooden fork towards the player)
  const aimAngle = isPulling ? Math.atan2(pouch.y - forkCenter.y, pouch.x - forkCenter.x) : Math.PI / 2;
  const tension = isPulling ? Math.min(1, pull / MAX_PULL) : 0;

  // Band width necks slightly under high tension
  const bandThickness = isPulling ? Math.max(3.0, 5.5 - tension * 2.0) : 5.0;
  const innerThickness = isPulling ? Math.max(1.3, 2.6 - tension * 1.0) : 2.4;

  // Elastic band colors: based on selected skin
  const outerBandColor = tension > 0.85 ? "#ff3b30" : skin.bandOuter;
  const midBandColor = tension > 0.85 ? "#ff9500" : skin.bandMid;
  const highlightBandColor = tension > 0.85 ? "#ffffff" : skin.bandHighlight;

  // Tangent perpendicular to pull direction (pouch width vector)
  const tanX = -Math.sin(aimAngle);
  const tanY = Math.cos(aimAngle);
  const pouchRadius = 9;

  // Attachment points on pouch for the two bands
  const pouchLeft = { x: pouch.x + tanX * pouchRadius, y: pouch.y + tanY * pouchRadius };
  const pouchRight = { x: pouch.x - tanX * pouchRadius, y: pouch.y - tanY * pouchRadius };

  // Left & Right rubber bands drawn on top of the wooden prongs
  if (skinId !== "classic") {
    ctx.save();
    ctx.shadowColor = skin.glowColor;
    ctx.shadowBlur = 8;
  }
  roundedLine(ctx, leftTip.x, leftTip.y, pouchLeft.x, pouchLeft.y, bandThickness + 2, outerBandColor);
  roundedLine(ctx, leftTip.x, leftTip.y, pouchLeft.x, pouchLeft.y, bandThickness, midBandColor);
  roundedLine(ctx, leftTip.x, leftTip.y, pouchLeft.x, pouchLeft.y, innerThickness, highlightBandColor);

  roundedLine(ctx, rightTip.x, rightTip.y, pouchRight.x, pouchRight.y, bandThickness + 2, outerBandColor);
  roundedLine(ctx, rightTip.x, rightTip.y, pouchRight.x, pouchRight.y, bandThickness, midBandColor);
  roundedLine(ctx, rightTip.x, rightTip.y, pouchRight.x, pouchRight.y, innerThickness, highlightBandColor);
  if (skinId !== "classic") {
    ctx.restore();
  }

  // Fork tip wraps (whipped cord binding the rubber bands to the carved prong notches)
  for (const tip of [leftTip, rightTip]) {
    ctx.fillStyle = skinId === "cyber" ? "#00f0ff" : skinId === "magma" ? "#ff4500" : "#301a0c";
    ctx.beginPath();
    ctx.arc(tip.x, tip.y + 1, 3.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = skinId === "gold" ? "#ffd700" : "#b88a56";
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(tip.x - 3, tip.y);
    ctx.lineTo(tip.x + 3, tip.y);
    ctx.moveTo(tip.x - 3, tip.y + 2.5);
    ctx.lineTo(tip.x + 3, tip.y + 2.5);
    ctx.stroke();
  }

  // 4. LEATHER CLOTH POUCH ("Kain Kulit Pelontar")
  ctx.save();
  ctx.translate(pouch.x, pouch.y);
  ctx.rotate(aimAngle + Math.PI / 2);

  const pw = 23;
  const ph = 15;

  // Back leather shadow
  ctx.fillStyle = skin.pouchOuter;
  ctx.beginPath();
  ctx.ellipse(0, 0, pw / 2 + 1.2, ph / 2 + 1.2, 0, 0, Math.PI * 2);
  ctx.fill();

  // Main saddle leather body
  const leatherGrad = ctx.createLinearGradient(0, -ph / 2, 0, ph / 2);
  leatherGrad.addColorStop(0, skin.pouchGrad1);
  leatherGrad.addColorStop(1, skin.pouchGrad2);
  ctx.fillStyle = leatherGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, pw / 2, ph / 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = skin.pouchBorder;
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Stitched border along perimeter with waxed thread
  ctx.strokeStyle = skin.pouchBorder;
  ctx.lineWidth = 1.0;
  ctx.setLineDash([2, 1.8]);
  ctx.beginPath();
  ctx.ellipse(0, 0, pw / 2 - 2, ph / 2 - 2, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Grommet eyelets where the rubber bands connect
  const drawGrommet = (gx: number) => {
    ctx.fillStyle = skin.grommetColor;
    ctx.beginPath();
    ctx.arc(gx, 0, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1e1108";
    ctx.beginPath();
    ctx.arc(gx, 0, 1.1, 0, Math.PI * 2);
    ctx.fill();
  };
  drawGrommet(-pw / 2 + 2.5);
  drawGrommet(pw / 2 - 2.5);

  // 5. LOADED PELLET OR JURUS AMMO (snugly nestled inside the pouch)
  if (!s.ball) {
    if (s.activeSkill === "super") {
      ctx.save();
      ctx.shadowColor = "#00f0ff";
      ctx.shadowBlur = 10;
      const grad = ctx.createRadialGradient(0, 0, 2, 0, 0, 9);
      grad.addColorStop(0, "#ffffff");
      grad.addColorStop(0.4, "#00f0ff");
      grad.addColorStop(1, "#0077b6");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, 8.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.arc(0, 0, 11, s.time * 6, s.time * 6 + Math.PI);
      ctx.stroke();
      ctx.restore();
    } else if (s.activeSkill === "bomb") {
      // Translucent Water Balloon (Balon Bola Air)
      if (art.waterBalloon) {
        ctx.drawImage(art.waterBalloon, -11, -13, 22, 22);
      } else {
        ctx.save();
        ctx.shadowColor = "#00d2ff";
        ctx.shadowBlur = 8;
        const waterGrad = ctx.createRadialGradient(-3, -3, 2, 0, 0, 10);
        waterGrad.addColorStop(0, "#e0f8ff");
        waterGrad.addColorStop(0.5, "#00c3ff");
        waterGrad.addColorStop(1, "#0077b6");
        ctx.fillStyle = waterGrad;
        ctx.beginPath();
        ctx.arc(0, 0, 9.5, 0, Math.PI * 2);
        ctx.fill();
        // Balloon knot at top
        ctx.fillStyle = "#0096c7";
        ctx.beginPath();
        ctx.arc(0, -10, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    } else {
      if (art.pellet) {
        ctx.drawImage(art.pellet, -9, -9, 18, 18);
      } else {
        ctx.fillStyle = "#ffdd00";
        ctx.strokeStyle = "#d49a00";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(0, 0, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Front leather lip cupping the lower rim of the pellet
    ctx.fillStyle = skin.pouchOuter;
    ctx.beginPath();
    ctx.arc(0, 2.2, 8.5, 0, Math.PI);
    ctx.fill();
    ctx.strokeStyle = skin.pouchBorder;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.restore(); // Restore pouch rotation


  // 6. DYNAMIC PULL TENSION RING
  if (isPulling && pull > 18) {
    const ringColor = tension > 0.85 ? "#ff3b30" : tension > 0.5 ? "#ffd152" : "#76d275";
    ctx.strokeStyle = ringColor;
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.arc(pouch.x, pouch.y, 25, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * tension);
    ctx.stroke();
  }

  ctx.restore();
}

function drawUniqueObstacles(ctx: CanvasRenderingContext2D, obstacles: UniqueObstacle[], time: number, isVolcano: boolean) {
  for (const uo of obstacles) {
    if (uo.broken) continue;

    ctx.save();
    if (uo.wobble > 0) {
      const shakeX = Math.sin(time * 35) * uo.wobble * 3.5;
      ctx.translate(shakeX, 0);
    }

    if (uo.kind === "bumper_crystal") {
      // 1. PINBALL BUMPER CRYSTAL (Emerald in jungle, Fiery Magma in volcano)
      ctx.save();
      ctx.translate(uo.x, uo.y);
      const pulse = 1 + Math.sin(time * 3 + uo.phase) * 0.08;
      ctx.scale(pulse, pulse);

      const r = uo.radius;
      // Glowing aura
      ctx.shadowColor = isVolcano ? "#ff4500" : "#00f0ff";
      ctx.shadowBlur = uo.hitFlash > 0 ? 28 : 14;

      // Outer rotating dashed ring
      ctx.strokeStyle = isVolcano ? "rgba(255, 120, 40, 0.6)" : "rgba(0, 240, 255, 0.6)";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(0, 0, r + 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Crystal hexagonal body
      const grad = ctx.createRadialGradient(0, 0, 2, 0, 0, r);
      if (uo.hitFlash > 0) {
        grad.addColorStop(0, "#ffffff");
        grad.addColorStop(0.5, isVolcano ? "#ffdd55" : "#a5f3fc");
        grad.addColorStop(1, isVolcano ? "#ff5722" : "#0284c7");
      } else if (isVolcano) {
        grad.addColorStop(0, "#ffeaa7");
        grad.addColorStop(0.4, "#ff7675");
        grad.addColorStop(0.85, "#d63031");
        grad.addColorStop(1, "#2d3436");
      } else {
        grad.addColorStop(0, "#e0f2fe");
        grad.addColorStop(0.35, "#38bdf8");
        grad.addColorStop(0.8, "#0284c7");
        grad.addColorStop(1, "#0369a1");
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      for (let a = 0; a < 6; a++) {
        const ang = (a * Math.PI) / 3 + time * 0.6 + uo.phase;
        const px = Math.cos(ang) * r;
        const py = Math.sin(ang) * r;
        if (a === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();

      // Crystal facet highlights
      ctx.strokeStyle = uo.hitFlash > 0 ? "#ffffff" : isVolcano ? "#ffd600" : "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Core center star
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(0, 0, 4, 0, Math.PI * 2);
      ctx.fill();

      // Bumper floating label
      ctx.shadowBlur = 0;
      ctx.font = "900 9.5px system-ui";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = isVolcano ? "#ffbe76" : "#7dd3fc";
      ctx.fillText("BUMPER", 0, r + 11);

      ctx.restore();
    } else if (uo.kind === "beehive") {
      // 2. SWINGING BEEHIVE (Jungle honeycomb with vine & cute bees)
      // Hanging vine from anchor
      ctx.save();
      ctx.strokeStyle = "#2d5a27";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(uo.anchorX, uo.anchorY);
      const midY = (uo.anchorY + uo.y - uo.radius) / 2;
      ctx.quadraticCurveTo(uo.anchorX + Math.sin(time * 2 + uo.phase) * 6, midY, uo.x, uo.y - uo.radius);
      ctx.stroke();

      // Vine leaves
      ctx.fillStyle = "#4ea048";
      ctx.beginPath();
      ctx.ellipse(uo.anchorX, uo.anchorY + 12, 6, 3, 0.4, 0, Math.PI * 2);
      ctx.ellipse(uo.anchorX + 4, midY, 6, 3, -0.4, 0, Math.PI * 2);
      ctx.fill();

      // Hive Body (Layered horizontal rounded honeycombs)
      ctx.translate(uo.x, uo.y);
      const hiveGrad = ctx.createLinearGradient(0, -uo.radius, 0, uo.radius);
      hiveGrad.addColorStop(0, "#fbbf24");
      hiveGrad.addColorStop(0.5, "#d97706");
      hiveGrad.addColorStop(1, "#92400e");
      ctx.fillStyle = hiveGrad;
      ctx.strokeStyle = "#78350f";
      ctx.lineWidth = 1.5;

      // Tier 1 (top cap)
      ctx.beginPath();
      ctx.roundRect(-12, -uo.radius, 24, 10, 5);
      ctx.fill();
      ctx.stroke();

      // Tier 2 (middle wide)
      ctx.beginPath();
      ctx.roundRect(-uo.radius, -uo.radius + 8, uo.radius * 2, 14, 7);
      ctx.fill();
      ctx.stroke();

      // Tier 3 (lower taper)
      ctx.beginPath();
      ctx.roundRect(-15, -uo.radius + 20, 30, 11, 5);
      ctx.fill();
      ctx.stroke();

      // Tier 4 (bottom nub)
      ctx.beginPath();
      ctx.roundRect(-8, -uo.radius + 29, 16, 8, 4);
      ctx.fill();
      ctx.stroke();

      // Entrance hole
      ctx.fillStyle = "#291404";
      ctx.beginPath();
      ctx.arc(0, 2, 5, 0, Math.PI * 2);
      ctx.fill();

      // Orbiting cute bees
      for (let b = 0; b < 2; b++) {
        const bAng = time * 4 + b * Math.PI + uo.phase;
        const bx = Math.cos(bAng) * 26;
        const by = Math.sin(bAng * 1.3) * 14;
        // Bee yellow/black body
        ctx.fillStyle = "#fbbf24";
        ctx.beginPath();
        ctx.ellipse(bx, by, 4.5, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#1e1e1e";
        ctx.fillRect(bx - 1, by - 3, 2, 6);
        // Translucent wings fluttering
        ctx.fillStyle = "rgba(255, 255, 255, 0.75)";
        ctx.beginPath();
        ctx.ellipse(bx, by - 4, 2, 4, Math.sin(time * 30) * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    } else if (uo.kind === "totem_pillar") {
      // 3. TOTEM PILLAR / OBSIDIAN MONOLITH
      ctx.save();
      ctx.translate(uo.x, uo.y);
      const pw = 30;
      const ph = 74;

      // Drop shadow
      ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
      ctx.beginPath();
      ctx.ellipse(0, ph / 2 + 3, pw / 2 + 4, 6, 0, 0, Math.PI * 2);
      ctx.fill();

      // Stone block
      const stoneGrad = ctx.createLinearGradient(-pw / 2, 0, pw / 2, 0);
      if (isVolcano) {
        stoneGrad.addColorStop(0, "#181110");
        stoneGrad.addColorStop(0.3, "#341d1a");
        stoneGrad.addColorStop(0.7, "#422521");
        stoneGrad.addColorStop(1, "#181110");
      } else {
        stoneGrad.addColorStop(0, "#334155");
        stoneGrad.addColorStop(0.3, "#475569");
        stoneGrad.addColorStop(0.7, "#64748b");
        stoneGrad.addColorStop(1, "#1e293b");
      }
      ctx.fillStyle = stoneGrad;
      ctx.strokeStyle = isVolcano ? "#572118" : "#1e293b";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.roundRect(-pw / 2, -ph / 2, pw, ph, 5);
      ctx.fill();
      ctx.stroke();

      // Ancient engraved tiki face / glyphs
      const runeColor = isVolcano ? "#ff5722" : "#38bdf8";
      ctx.strokeStyle = runeColor;
      ctx.lineWidth = 1.8;
      ctx.shadowColor = runeColor;
      ctx.shadowBlur = uo.hitFlash > 0 ? 16 : 6;

      // Tiki Eyes
      ctx.fillStyle = runeColor;
      ctx.fillRect(-9, -20, 5, 4);
      ctx.fillRect(4, -20, 5, 4);

      // Tiki Mouth
      ctx.beginPath();
      ctx.moveTo(-8, -6);
      ctx.lineTo(8, -6);
      ctx.moveTo(-6, -10);
      ctx.lineTo(6, -10);
      ctx.stroke();

      // Body runic lines
      ctx.beginPath();
      ctx.moveTo(-8, 8);
      ctx.lineTo(0, 16);
      ctx.lineTo(8, 8);
      ctx.moveTo(0, 16);
      ctx.lineTo(0, 28);
      ctx.stroke();

      ctx.restore();
    }

    ctx.restore();
  }
}

function drawWindStreaks(ctx: CanvasRenderingContext2D, s: Scene, level: number) {
  const windMag = Math.hypot(s.windX, s.windY);
  if (windMag < 12) return;

  const isVolcano = level >= 11;
  const angle = Math.atan2(s.windY, s.windX);
  const lengthScale = Math.min(3.2, windMag / 60);

  ctx.save();
  ctx.lineCap = "round";

  for (const streak of s.windStreaks) {
    const len = streak.len * lengthScale;
    const startX = streak.x;
    const startY = streak.y;
    const endX = startX - Math.cos(angle) * len;
    const endY = startY - Math.sin(angle) * len;

    const grad = ctx.createLinearGradient(startX, startY, endX, endY);
    if (isVolcano) {
      grad.addColorStop(0, `rgba(255, 185, 80, ${streak.alpha * 0.85})`);
      grad.addColorStop(0.65, `rgba(255, 95, 30, ${streak.alpha * 0.5})`);
      grad.addColorStop(1, "rgba(255, 40, 0, 0)");
    } else {
      grad.addColorStop(0, `rgba(225, 250, 255, ${streak.alpha * 0.85})`);
      grad.addColorStop(0.65, `rgba(165, 235, 255, ${streak.alpha * 0.45})`);
      grad.addColorStop(1, "rgba(255, 255, 255, 0)");
    }

    ctx.strokeStyle = grad;
    ctx.lineWidth = streak.width * 1.35;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  }

  ctx.restore();
}

function drawWindHudAndBanner(ctx: CanvasRenderingContext2D, s: Scene, level: number) {
  const windCfg = windConfigForLevel(level);
  if (!windCfg.enabled) return;

  const isVolcano = level >= 11;
  const windMag = Math.hypot(s.windX, s.windY);

  // 1. Persistent Wind Indicator Bar / Compass at top center (y = 56)
  ctx.save();
  const hudX = W / 2;
  const hudY = 56;
  const hudW = 196;
  const hudH = 22;

  // Background pill
  ctx.fillStyle = isVolcano ? "rgba(35, 14, 8, 0.75)" : "rgba(16, 36, 24, 0.75)";
  ctx.strokeStyle = isVolcano ? "rgba(255, 120, 50, 0.5)" : "rgba(90, 210, 140, 0.5)";
  ctx.lineWidth = 1.2;

  ctx.beginPath();
  ctx.roundRect(hudX - hudW / 2, hudY - hudH / 2, hudW, hudH, 11);
  ctx.fill();
  ctx.stroke();

  // Compass circle & arrow
  const compassX = hudX - hudW / 2 + 16;
  const compassY = hudY;

  ctx.fillStyle = isVolcano ? "rgba(255, 90, 30, 0.35)" : "rgba(70, 190, 130, 0.35)";
  ctx.beginPath();
  ctx.arc(compassX, compassY, 7, 0, Math.PI * 2);
  ctx.fill();

  if (windMag > 15) {
    const angle = Math.atan2(s.windY, s.windX);
    ctx.save();
    ctx.translate(compassX, compassY);
    ctx.rotate(angle);

    // Arrow pointing in wind direction
    ctx.fillStyle = isVolcano ? "#ffdd55" : "#55ffaa";
    ctx.beginPath();
    ctx.moveTo(5, 0);
    ctx.lineTo(-3, -3.5);
    ctx.lineTo(-1, 0);
    ctx.lineTo(-3, 3.5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  } else {
    // Calm dot
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(compassX, compassY, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Label text
  ctx.font = "800 11px system-ui";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  if (windMag < 18) {
    ctx.fillStyle = "#e2e8f0";
    const pauseSec = Math.max(0, Math.ceil(windCfg.waveInterval - s.windTimer));
    ctx.fillText(`ANGIN TENANG (JEDA ${pauseSec}s) 🍃`, compassX + 12, hudY);
  } else {
    const mps = Math.round(windMag / 10);
    let dirLabel = "";
    if (s.windWaveType === "right") dirLabel = "KANAN ➡";
    else if (s.windWaveType === "left") dirLabel = "KIRI ⬅";
    else if (s.windWaveType === "down") dirLabel = "BAWAH ⬇";
    else if (s.windWaveType === "down-right") dirLabel = "BWH-KNN ↘";
    else if (s.windWaveType === "down-left") dirLabel = "BWH-KRI ↙";
    else dirLabel = s.windX > 0 ? "KANAN ➡" : "KIRI ⬅";

    ctx.fillStyle = isVolcano ? "#ffbe76" : "#7bed9f";
    ctx.fillText(`${mps} m/s • ${dirLabel}`, compassX + 12, hudY);
  }
  ctx.restore();

  // 2. Animated Wind Wave Announcement Banner
  if (s.windBannerTimer > 0 && s.windBannerText) {
    ctx.save();
    const alpha = Math.min(1, s.windBannerTimer * 1.6);
    const scale = 1 + Math.sin((1.6 - s.windBannerTimer) * Math.PI) * 0.06;
    const bannerY = 114;

    ctx.translate(W / 2, bannerY);
    ctx.scale(scale, scale);
    ctx.globalAlpha = alpha;

    ctx.font = "900 13.5px system-ui";
    const textWidth = ctx.measureText(s.windBannerText).width;
    const bW = textWidth + 34;
    const bH = 28;

    // Glowing background
    ctx.shadowColor = isVolcano ? "#ff4500" : "#00f0ff";
    ctx.shadowBlur = 14;

    ctx.fillStyle = isVolcano ? "rgba(45, 10, 5, 0.88)" : "rgba(10, 28, 38, 0.88)";
    ctx.strokeStyle = isVolcano ? "#ff9f43" : "#48dbfb";
    ctx.lineWidth = 1.8;

    ctx.beginPath();
    ctx.roundRect(-bW / 2, -bH / 2, bW, bH, 14);
    ctx.fill();
    ctx.stroke();

    // Banner Text
    ctx.shadowBlur = 0;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 3;
    ctx.strokeText(s.windBannerText, 0, 1);
    ctx.fillStyle = isVolcano ? "#ffdd59" : "#ffffff";
    ctx.fillText(s.windBannerText, 0, 1);

    ctx.restore();
  }
}

function draw(ctx: CanvasRenderingContext2D, s: Scene, level: number, art: GameArt) {
  ctx.save();
  if (s.screenShake > 0) {
    const ox = (Math.random() - 0.5) * s.screenShake * 2;
    const oy = (Math.random() - 0.5) * s.screenShake * 2;
    ctx.translate(ox, oy);
  }

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "rgba(8,44,35,.15)";
  ctx.fillRect(0, 0, W, H);

  const nervousIndices = new Set<number>();
  let trajectoryDots: { x: number; y: number; alpha: number; r: number }[] = [];

  // PARABOLIC TRAJECTORY PREDICTION ARC
  if (s.aim && !s.ball) {
    const dx = s.sling.x - s.aim.x;
    const dy = s.sling.y - s.aim.y;
    const pullLen = Math.hypot(dx, dy);
    if (pullLen > 16) {
      const isSuper = s.activeSkill === "super";
      const isBomb = s.activeSkill === "bomb";
      const speed = (430 + Math.min(pullLen, MAX_PULL) * 2.6) * (isSuper ? 1.35 : 1.0);
      const vx = (dx / pullLen) * speed;
      const vy = (dy / pullLen) * speed;

      const dots: { x: number; y: number; alpha: number; r: number }[] = [];
      let simX = s.sling.x;
      let simY = s.sling.y;
      let simVx = vx;
      let simVy = vy;
      const simDt = 0.035;
      let hitObstacle = false;

      for (let step = 0; step < 28 && !hitObstacle; step++) {
        simVx += s.windX * simDt;
        simX += simVx * simDt;
        simY += simVy * simDt;
        simVy += (420 + s.windY) * simDt;

        for (let mIdx = 0; mIdx < s.monkeys.length; mIdx++) {
          const m = s.monkeys[mIdx];
          if (!m.hit && !(m.behavior === "peek" && m.peekPhase === "hidden")) {
            const mp = monkeyPosition(m, s.time);
            if (Math.hypot(simX - mp.x, simY - (mp.y - 10)) < 65) {
              nervousIndices.add(mIdx);
            }
          }
        }

        if (!isSuper) {
          for (const fb of s.fallingBranches) {
            const seg = getBranchSegment(fb);
            if (segmentDistance(simX, simY, seg.x1, seg.y1, seg.x2, seg.y2) < seg.radius + 8) {
              hitObstacle = true;
              break;
            }
          }
          for (const uo of s.uniqueObstacles) {
            if (!uo.broken && uo.kind === "totem_pillar" && Math.abs(simX - uo.x) < 20 && Math.abs(simY - uo.y) < 42) {
              hitObstacle = true;
              break;
            }
          }
        }

        if (simX < 0 || simX > W || simY < 0 || simY > H) break;

        const progress = step / 27;
        dots.push({
          x: simX,
          y: simY,
          alpha: Math.max(0.25, 1 - progress * 0.7),
          r: Math.max(2.4, 5.2 - progress * 2.8),
        });
      }
      trajectoryDots = dots;

      // If aiming with Bomb, highlight all monkeys within 95px blast radius of trajectory end
      if (isBomb && dots.length > 0) {
        const lastDot = dots[dots.length - 1];
        for (let mIdx = 0; mIdx < s.monkeys.length; mIdx++) {
          const m = s.monkeys[mIdx];
          if (!m.hit && !(m.behavior === "peek" && m.peekPhase === "hidden")) {
            const mp = monkeyPosition(m, s.time);
            if (Math.hypot(lastDot.x - mp.x, lastDot.y - (mp.y - 10)) < 95) {
              nervousIndices.add(mIdx);
            }
          }
        }
      }
    }
  }

  // Draw perches and monkeys
  for (let mIdx = 0; mIdx < s.monkeys.length; mIdx++) {
    const m = s.monkeys[mIdx];
    const p = monkeyPosition(m, s.time);
    if (art.branch) {
      if (m.behavior === "idle") {
        ctx.drawImage(art.branch, m.baseX - 60, m.baseY + 36, 120, 36);
      } else if (m.behavior === "patrol") {
        const perchSpan = (m.patrolDistance + 24) * 2;
        ctx.drawImage(art.branch, m.baseX - perchSpan / 2, m.baseY + 36, perchSpan, 36);
      }
    }
    drawMonkey(ctx, m, p.x, p.y, s.time, art, nervousIndices.has(mIdx));
  }

  // Draw tree trunks / volcanic pillars OVER hiding monkeys
  drawTreeCovers(ctx, s.trees, s.time, level >= 11);

  // Draw monkey chat bubbles (crisp and visible above tree cover)
  for (let mIdx = 0; mIdx < s.monkeys.length; mIdx++) {
    const m = s.monkeys[mIdx];
    const p = monkeyPosition(m, s.time);
    drawMonkeyChatBubble(ctx, m, p.x, p.y, s.time, nervousIndices.has(mIdx));
  }

  // Draw falling obstacle branches or volcanic stalactites
  drawFallingBranches(ctx, s.fallingBranches, art, s.time, level >= 11);

  // Draw unique interactive obstacles (crystal bumpers, swinging beehives, totem pillars)
  drawUniqueObstacles(ctx, s.uniqueObstacles, s.time, level >= 11);

  // Draw tumbling monkeys
  drawTumblingMonkeys(ctx, s, art);

  // Draw predicted trajectory dots
  const isSuperAim = s.activeSkill === "super";
  const isBombAim = s.activeSkill === "bomb";
  const dotColor = isSuperAim ? "0, 240, 255" : isBombAim ? "0, 210, 255" : "255, 220, 40";
  for (const dot of trajectoryDots) {
    ctx.fillStyle = `rgba(${dotColor}, ${dot.alpha})`;
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, isSuperAim ? dot.r * 1.2 : dot.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Draw Water Balloon Splash Reticle at the end of the shot direction
  if (isBombAim && trajectoryDots.length > 0) {
    const endDot = trajectoryDots[trajectoryDots.length - 1];
    const pulse = 1 + 0.05 * Math.sin(s.time * 8);
    const blastRadius = 95 * pulse;

    ctx.save();
    // Glowing AOE water splash zone fill
    const splashGrad = ctx.createRadialGradient(endDot.x, endDot.y, 8, endDot.x, endDot.y, blastRadius);
    splashGrad.addColorStop(0, "rgba(0, 210, 255, 0.32)");
    splashGrad.addColorStop(0.65, "rgba(0, 160, 255, 0.15)");
    splashGrad.addColorStop(1, "rgba(0, 230, 255, 0)");
    ctx.fillStyle = splashGrad;
    ctx.beginPath();
    ctx.arc(endDot.x, endDot.y, blastRadius, 0, Math.PI * 2);
    ctx.fill();

    // Dashed water ripple border
    ctx.strokeStyle = "rgba(0, 220, 255, 0.9)";
    ctx.lineWidth = 2.2;
    ctx.setLineDash([7, 4]);
    ctx.beginPath();
    ctx.arc(endDot.x, endDot.y, blastRadius, 0, Math.PI * 2);
    ctx.stroke();
    // Concentric inner water wave ripple
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = "rgba(165, 243, 252, 0.65)";
    ctx.beginPath();
    ctx.arc(endDot.x, endDot.y, blastRadius * 0.52, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Crosshair target icon
    ctx.strokeStyle = "#00d2ff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(endDot.x - 16, endDot.y); ctx.lineTo(endDot.x + 16, endDot.y);
    ctx.moveTo(endDot.x, endDot.y - 16); ctx.lineTo(endDot.x, endDot.y + 16);
    ctx.stroke();

    // Center water droplet badge
    ctx.fillStyle = "#0096c7";
    ctx.beginPath();
    ctx.arc(endDot.x, endDot.y, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.font = "bold 13px system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("💧", endDot.x, endDot.y);

    // Pill label above target
    ctx.font = "900 10.5px system-ui, sans-serif";
    ctx.fillStyle = "#a5f3fc";
    ctx.fillText("AREA CIPRATAN 💦", endDot.x, endDot.y - 22);
    ctx.restore();
  }

  // Draw ambient drifting jungle leaves & wind streaks
  for (const leaf of s.driftingLeaves) {
    ctx.save();
    ctx.translate(leaf.x, leaf.y);
    ctx.rotate(leaf.rot);
    ctx.fillStyle = leaf.color;
    ctx.beginPath();
    ctx.ellipse(0, 0, leaf.size * 0.45, leaf.size, 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  drawWindStreaks(ctx, s, level);


  // Slingshot & grounded jungle / volcanic base
  drawSlingshot(ctx, s, art, s.skinId, level >= 11);

  // Flying pellet or special skill projectile with trail
  if (s.ball) {
    const isSuper = s.ball.kind === "super";
    const isBomb = s.ball.kind === "bomb";

    for (let i = 0; i < s.ball.trail.length; i++) {
      const p = s.ball.trail[i];
      const prog = i / s.ball.trail.length;
      if (isSuper) {
        ctx.fillStyle = `rgba(0, 240, 255, ${prog * 0.85})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4 + prog * 4, 0, Math.PI * 2);
        ctx.fill();
      } else if (isBomb) {
        ctx.fillStyle = i % 2 === 0 ? `rgba(0, 210, 255, ${prog * 0.85})` : `rgba(165, 243, 252, ${prog * 0.7})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3 + prog * 4, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = `rgba(255, 220, 40, ${prog * 0.65})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3 + prog * 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    if (isSuper) {
      ctx.save();
      ctx.shadowColor = "#00f0ff";
      ctx.shadowBlur = 16;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(s.ball.x, s.ball.y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#00f0ff";
      ctx.lineWidth = 3.5;
      ctx.stroke();
      ctx.restore();
    } else if (isBomb) {
      ctx.save();
      ctx.translate(s.ball.x, s.ball.y);
      // Fluid wobble in flight
      const wobble = Math.sin(s.time * 26) * 0.08;
      ctx.scale(1 + wobble, 1 - wobble);
      if (art.waterBalloon) {
        ctx.drawImage(art.waterBalloon, -15, -17, 30, 30);
      } else {
        ctx.shadowColor = "#00d2ff";
        ctx.shadowBlur = 10;
        const waterGrad = ctx.createRadialGradient(-3, -3, 2, 0, 0, 11);
        waterGrad.addColorStop(0, "#e0f8ff");
        waterGrad.addColorStop(0.5, "#00c3ff");
        waterGrad.addColorStop(1, "#0077b6");
        ctx.fillStyle = waterGrad;
        ctx.beginPath();
        ctx.arc(0, 0, 11, 0, Math.PI * 2);
        ctx.fill();
        // Tied knot
        ctx.fillStyle = "#0096c7";
        ctx.beginPath();
        ctx.arc(0, -11, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    } else {
      if (art.pellet) {
        ctx.save();
        ctx.translate(s.ball.x, s.ball.y);
        ctx.rotate((s.ball.x + s.ball.y) * 0.045);
        ctx.drawImage(art.pellet, -14, -14, 28, 28);
        ctx.restore();
      } else {
        ctx.fillStyle = "#ffdd00";
        ctx.strokeStyle = "#d49a00";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(s.ball.x, s.ball.y, 11, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
  }

  // Particles
  for (const p of s.particles) {
    ctx.globalAlpha = Math.max(0, p.life / p.maxLife);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Impact feedback text
  if (s.impact) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, s.impact.life * 2);
    ctx.font = "900 26px system-ui";
    ctx.textAlign = "center";
    ctx.lineWidth = 5;
    ctx.strokeStyle = "#382517";
    const textY = s.impact.y - (1 - s.impact.life) * 35;
    ctx.strokeText(s.impact.text, s.impact.x, textY);
    ctx.fillStyle = s.impact.color;
    ctx.fillText(s.impact.text, s.impact.x, textY);

    if (s.impact.subtext) {
      ctx.font = "900 18px system-ui";
      ctx.strokeStyle = "#382517";
      ctx.strokeText(s.impact.subtext, s.impact.x, textY + 22);
      ctx.fillStyle = "#ffd166";
      ctx.fillText(s.impact.subtext, s.impact.x, textY + 22);
    }
    ctx.restore();
  }

  // Persistent Wind Compass HUD & Wave Announcement Banner
  drawWindHudAndBanner(ctx, s, level);

  ctx.restore();
}

function useSound(enabled: boolean) {
  const audio = useRef<AudioContext | null>(null);
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  return useCallback((kind: "shoot" | "hit" | "wood" | "ricochet" | "miss" | "peek" | "bossHit" | "bossDefeat" | "clear" | "lost" | "laugh" | "shatter" | "bonus" | "dodge" | "laser" | "bomb" | "explosion" | "wind") => {
    if (!enabledRef.current) return;
    try {
      const ctx = (audio.current ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)());
      if (ctx.state === "suspended") void ctx.resume();
      const now = ctx.currentTime;

      if (kind === "shoot") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(520, now);
        osc.frequency.exponentialRampToValueAtTime(75, now + 0.12);
        gain.gain.setValueAtTime(0.22, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.15);
      } else if (kind === "laser") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(1450, now);
        osc.frequency.exponentialRampToValueAtTime(320, now + 0.2);
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.23);
      } else if (kind === "bomb") {
        // Water balloon launch "whish-bloop"
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(380, now);
        osc.frequency.exponentialRampToValueAtTime(160, now + 0.16);
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.2);
      } else if (kind === "explosion") {
        // Water balloon splash / BYUURR! burst
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(260, now);
        osc.frequency.exponentialRampToValueAtTime(55, now + 0.38);
        gain.gain.setValueAtTime(0.38, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.45);

        // Water splash droplet chirp
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = "triangle";
        osc2.frequency.setValueAtTime(680, now);
        osc2.frequency.exponentialRampToValueAtTime(180, now + 0.24);
        gain2.gain.setValueAtTime(0.22, now);
        gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.26);
        osc2.connect(gain2).connect(ctx.destination);
        osc2.start(now);
        osc2.stop(now + 0.28);
      } else if (kind === "hit") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(340, now);
        osc.frequency.exponentialRampToValueAtTime(920, now + 0.12);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.19);
      } else if (kind === "wood" || kind === "ricochet") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(kind === "ricochet" ? 520 : 360, now);
        osc.frequency.exponentialRampToValueAtTime(kind === "ricochet" ? 220 : 140, now + 0.08);
        gain.gain.setValueAtTime(0.24, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.09);
      } else if (kind === "miss") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(380, now);
        osc.frequency.exponentialRampToValueAtTime(110, now + 0.22);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.24);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.25);
      } else if (kind === "bossHit") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(120, now);
        osc.frequency.exponentialRampToValueAtTime(45, now + 0.22);
        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.26);
      } else if (kind === "bossDefeat") {
        [523.25, 659.25, 783.99, 1046.5].forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "triangle";
          osc.frequency.setValueAtTime(freq, now + idx * 0.08);
          gain.gain.setValueAtTime(0.18, now + idx * 0.08);
          gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.08 + 0.35);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + idx * 0.08);
          osc.stop(now + idx * 0.08 + 0.36);
        });
      } else if (kind === "clear") {
        [523.25, 659.25, 783.99].forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now + idx * 0.09);
          gain.gain.setValueAtTime(0.15, now + idx * 0.09);
          gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.09 + 0.25);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + idx * 0.09);
          osc.stop(now + idx * 0.09 + 0.26);
        });
      } else if (kind === "peek") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(520, now);
        osc.frequency.exponentialRampToValueAtTime(780, now + 0.1);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.13);
      } else if (kind === "lost") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(240, now);
        osc.frequency.linearRampToValueAtTime(140, now + 0.45);
        gain.gain.setValueAtTime(0.18, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.48);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.5);
      } else if (kind === "laugh") {
        const notes = [580, 800, 640, 920, 720, 960];
        notes.forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "triangle";
          const t = now + idx * 0.055;
          osc.frequency.setValueAtTime(freq, t);
          osc.frequency.exponentialRampToValueAtTime(freq * 1.15, t + 0.045);
          gain.gain.setValueAtTime(0.14, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t);
          osc.stop(t + 0.055);
        });
      } else if (kind === "shatter") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(460, now);
        osc.frequency.exponentialRampToValueAtTime(95, now + 0.16);
        gain.gain.setValueAtTime(0.26, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.2);
      } else if (kind === "bonus") {
        [659.25, 880, 1318.5].forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "triangle";
          const t = now + idx * 0.065;
          osc.frequency.setValueAtTime(freq, t);
          gain.gain.setValueAtTime(0.16, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t);
          osc.stop(t + 0.3);
        });
      } else if (kind === "dodge") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(620, now);
        osc.frequency.exponentialRampToValueAtTime(220, now + 0.09);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.11);
      } else if (kind === "wind") {
        const bufferSize = Math.floor(ctx.sampleRate * 0.45);
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
        const noise = ctx.createBufferSource();
        noise.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = "bandpass";
        filter.frequency.setValueAtTime(320, now);
        filter.frequency.exponentialRampToValueAtTime(760, now + 0.15);
        filter.frequency.exponentialRampToValueAtTime(240, now + 0.42);
        filter.Q.setValueAtTime(2.2, now);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.13, now + 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.44);
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        noise.start(now);
        noise.stop(now + 0.45);
      }
    } catch {
      /* Audio is optional */
    }
  }, []);
}

export function TembakMonyetnyaPage() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const art = useRef<GameArt>({
    monkeyFront: null,
    monkeyBack: null,
    monkeyHit: null,
    monkeyBoss: null,
    slingshot: null,
    branch: null,
    pellet: null,
    waterBalloon: null,
  });
  const [activeSkin, setActiveSkinState] = useState<SlingshotSkinId>(() => getActiveSkin());
  const scene = useRef<Scene>(makeScene(1, getActiveSkin()));
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [level, setLevel] = useState(1);
  const [shots, setShots] = useState(makeScene(1).shots);
  const [remaining, setRemaining] = useState(makeScene(1).monkeys.length);
  const [timeLeft, setTimeLeft] = useState(makeScene(1).timeLeft);
  const lastSecRef = useRef(Math.ceil(makeScene(1).timeLeft));
  const [lossReason, setLossReason] = useState<"ammo" | "time" | null>(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [stars, setStars] = useState(3);
  const [mode, setMode] = useState<"menu" | "playing" | "cleared" | "lost" | "won">("menu");
  const modeRef = useRef(mode);
  const [soundOn, setSoundOn] = useState(true);
  const playSound = useSound(soundOn);

  // TikTok Mini Game & Customization states
  const [activeModal, setActiveModal] = useState<"skins" | "levels" | "ad" | null>(null);
  const [ammoSuper, setAmmoSuper] = useState<number>(() => getStoredSkillAmmo("super"));
  const [ammoBomb, setAmmoBomb] = useState<number>(() => getStoredSkillAmmo("bomb"));
  const [activeSkill, setActiveSkill] = useState<SkillType | null>(null);
  const [highScore, setHighScoreState] = useState<number>(() => getHighScore());
  const [maxUnlockedLevel, setMaxUnlockedLevelState] = useState<number>(() => getMaxUnlockedLevel());

  // Rewarded Video Ad simulation / playback state
  const [adRewardTarget, setAdRewardTarget] = useState<"super" | "bomb" | { skinId: SlingshotSkinId }>("super");
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(3);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Procedural Slow BGM Synchronization (100% Open Source & Commercial Safe)
  useEffect(() => {
    setTembakMonyetBgmEnabled(soundOn);
  }, [soundOn]);

  useEffect(() => {
    setTembakMonyetBgmMode(mode);
  }, [mode]);

  useEffect(() => {
    const handleUserGesture = () => {
      if (soundOn) {
        startTembakMonyetBgm();
      }
    };

    window.addEventListener("pointerdown", handleUserGesture, { passive: true });
    window.addEventListener("keydown", handleUserGesture, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
      stopTembakMonyetBgm();
    };
  }, [soundOn]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        setTembakMonyetBgmEnabled(false);
      } else {
        setTembakMonyetBgmEnabled(soundOn);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [soundOn]);

  useEffect(() => {
    applyPageMetadata({
      title: "Tembak Monyetnya! | Steria House",
      description: "Game ketapel 20 babak di hutan tropis & kawah vulkanik. Bidik monyet, manfaatkan pantulan ranting, dan kalahkan Kaisar Monyet Purba!",
      canonicalPath: "/games/tembak-monyetnya",
    });
  }, []);

  useEffect(() => {
    const images: HTMLImageElement[] = [];
    for (const key of Object.keys(SPRITE_FILES) as (keyof GameArt)[]) {
      const image = new Image();
      image.onload = () => { art.current[key] = image; };
      image.src = `/games/tembak-monyetnya/${SPRITE_FILES[key]}`;
      images.push(image);
    }
    return () => { for (const image of images) image.onload = null; };
  }, []);

  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (!ctx) return;
    let frame = 0;
    let previous = 0;

    const tick = (stamp: number) => {
      const rawDt = Math.min((stamp - (previous || stamp)) / 1000, 0.035);
      previous = stamp;
      const s = scene.current;

      if (s.bulletTime < 1.0) {
        s.bulletTime = Math.min(1.0, s.bulletTime + rawDt * 1.5);
      }
      const dt = rawDt * s.bulletTime;

      if (modeRef.current === "playing") {
        if (s.hitStop > 0) {
          s.hitStop -= dt;
        } else {
          s.time += dt;
          s.recoil = Math.max(0, s.recoil - dt * 5);
          s.screenShake = Math.max(0, s.screenShake - dt * 18);

          // Timer countdown (only runs during active play before clear delay)
          if (s.clearDelay <= 0 && s.monkeys.some(m => !m.hit)) {
            s.timeLeft = Math.max(0, s.timeLeft - dt);
            const currentSec = Math.ceil(s.timeLeft);
            if (currentSec !== lastSecRef.current) {
              lastSecRef.current = currentSec;
              setTimeLeft(currentSec);
            }
            if (s.timeLeft <= 0) {
              playSound("lost");
              setLossReason("time");
              setMode("lost");
            }
          }

          // Dynamic Wind Wave calculation (Waves every few seconds towards right/left/down)
          const windCfg = windConfigForLevel(level);
          if (windCfg.enabled && modeRef.current === "playing") {
            if (s.windWaveActive) {
              s.windWaveDuration -= dt;
              if (s.windWaveDuration <= 0) {
                // Wave surge ended, transition into exact 3-second calm pause
                s.windWaveActive = false;
                s.windWaveType = "calm";
                s.windTargetX = 0;
                s.windTargetY = 0;
                s.windTimer = 0; // RESET timer to 0 so the calm period lasts exactly 3.0 seconds!
              }
            } else {
              // Counting the 3.0-second calm window
              s.windTimer += dt;
              if (s.windTimer >= windCfg.waveInterval) {
                s.windTimer = 0;
                const dirs = windCfg.allowedDirections;
                if (dirs.length > 0) {
                  let nextDir = dirs[Math.floor(Math.random() * dirs.length)];
                  if (dirs.length > 1 && nextDir === s.windWaveType) {
                    nextDir = dirs[Math.floor(Math.random() * dirs.length)];
                  }
                  const strength = windCfg.gustStrength;
                  let vx = 0;
                  let vy = 0;
                  let banner = "";

                  if (nextDir === "right") {
                    vx = strength;
                    vy = 0;
                    banner = "💨 ANGIN KENCANG: KANAN ➡";
                  } else if (nextDir === "left") {
                    vx = -strength;
                    vy = 0;
                    banner = "💨 ANGIN KENCANG: KIRI ⬅";
                  } else if (nextDir === "down") {
                    vx = 0;
                    vy = strength * 1.15; // Downdraft pulls ball down towards ground
                    banner = "🌪️ HEMBUSAN BAWAH! ⬇";
                  } else if (nextDir === "down-right") {
                    vx = strength * 0.72;
                    vy = strength * 0.88;
                    banner = level >= 11 ? "🔥 BADAI ABU: BAWAH-KANAN ↘" : "🌪️ ANGIN: BAWAH-KANAN ↘";
                  } else if (nextDir === "down-left") {
                    vx = -strength * 0.72;
                    vy = strength * 0.88;
                    banner = level >= 11 ? "🔥 BADAI ABU: BAWAH-KIRI ↙" : "🌪️ ANGIN: BAWAH-KIRI ↙";
                  }

                  s.windWaveActive = true;
                  s.windWaveDuration = windCfg.waveDuration;
                  s.windWaveType = nextDir;
                  s.windTargetX = vx;
                  s.windTargetY = vy;
                  s.windBannerText = banner;
                  s.windBannerTimer = 1.6;

                  playSound("wind");
                }
              }
            }

            // Smooth responsive interpolation towards target wind vector
            s.windX += (s.windTargetX - s.windX) * dt * 6.0;
            s.windY += (s.windTargetY - s.windY) * dt * 6.0;
            s.wind = s.windX;

            if (s.windBannerTimer > 0) {
              s.windBannerTimer = Math.max(0, s.windBannerTimer - dt);
            }
          } else {
            s.windX = 0;
            s.windY = 0;
            s.wind = 0;
            s.windTargetX = 0;
            s.windTargetY = 0;
            s.windWaveActive = false;
            s.windBannerTimer = 0;
          }

          // Move wind streaks across the screen along wind vector
          const windMag = Math.hypot(s.windX, s.windY);
          if (windMag > 15) {
            const normX = s.windX / windMag;
            const normY = s.windY / windMag;
            for (const st of s.windStreaks) {
              st.x += (normX * (st.speed + windMag * 1.35)) * dt;
              st.y += (normY * (st.speed + windMag * 1.35)) * dt;
              if (st.x < -60) st.x = W + 50;
              if (st.x > W + 60) st.x = -50;
              if (st.y < -60) st.y = H + 50;
              if (st.y > H + 60) st.y = -50;
            }
          }

          // Drifting leaves / ash affected by 2D wind
          for (const leaf of s.driftingLeaves) {
            leaf.x += (leaf.vx + s.windX * 0.55) * dt;
            leaf.y += (leaf.vy + s.windY * 0.55) * dt;
            leaf.rot += (leaf.vRot + (s.windX + s.windY) * 0.015) * dt;
            if (leaf.x < -25) leaf.x = W + 25;
            if (leaf.x > W + 25) leaf.x = -25;
            if (leaf.y < -25) leaf.y = H + 25;
            if (leaf.y > H + 25) leaf.y = -25;
          }

          const branchCfg = fallingBranchConfigForLevel(level);
          for (const fb of s.fallingBranches) {
            fb.y += fb.vy * dt;
            fb.angle += fb.vRot * dt;
            if (fb.wobble > 0) fb.wobble = Math.max(0, fb.wobble - dt * 4);
            if (fb.y > H + 40) {
              const len = branchCfg.minLength + Math.random() * (branchCfg.maxLength - branchCfg.minLength || 1);
              fb.y = -50 - Math.random() * 80;
              fb.x = 80 + Math.random() * (W - 160);
              fb.length = len;
              fb.vy = branchCfg.minSpeed + Math.random() * (branchCfg.maxSpeed - branchCfg.minSpeed || 1);
              fb.angle = ((Math.random() - 0.5) * 45 * Math.PI) / 180;
              fb.vRot = (Math.random() - 0.5) * 0.35;
              fb.wobble = 0;
            }
          }

          // Update unique interactive obstacles
          for (const uo of s.uniqueObstacles) {
            if (uo.hitFlash > 0) uo.hitFlash = Math.max(0, uo.hitFlash - dt * 3);
            if (uo.wobble > 0) uo.wobble = Math.max(0, uo.wobble - dt * 3.5);

            if (uo.kind === "beehive" && !uo.broken) {
              uo.x = uo.baseX + Math.sin(s.time * 2.2 + uo.phase) * 18 + uo.wobble * 8 * Math.sin(s.time * 16);
              uo.y = uo.baseY + Math.cos(s.time * 1.8 + uo.phase) * 5;
            }
          }

          for (const m of s.monkeys) {
            if (m.hitFlash > 0) m.hitFlash = Math.max(0, m.hitFlash - dt * 3);
            if (m.dodgeTime > 0) m.dodgeTime = Math.max(0, m.dodgeTime - dt);
            if (m.laughTimer > 0) m.laughTimer = Math.max(0, m.laughTimer - dt);
            if (m.hit) continue;

            if (m.behavior === "patrol") {
              const spd = m.patrolSpeed / 25;
              const prevSin = Math.sin((s.time - dt) * spd + m.phase);
              const currSin = Math.sin(s.time * spd + m.phase);
              const vx = (currSin - prevSin) / dt;
              if (Math.abs(vx) > 0.05) {
                m.facingLeft = vx < 0;
              }
              m.walkStep += Math.abs(vx) * dt * 0.18;
            } else if (m.behavior === "peek") {
              const cyclePeriod = 5.2;
              const t = (s.time + m.phase * 1.5) % cyclePeriod;
              const prevPhase = m.peekPhase;
              if (t < 1.6) {
                m.peekPhase = "hidden";
              } else if (t < 2.3) {
                m.peekPhase = "warning";
                if (prevPhase !== "warning") playSound("peek");
              } else if (t < 2.7) {
                m.peekPhase = "peeking";
              } else if (t < 4.6) {
                m.peekPhase = "exposed";
              } else {
                m.peekPhase = "retreating";
              }
              m.facingLeft = m.hideSide === "right";
            }
          }

          if (s.ball) {
            const b = s.ball;

            const explodeAt = (bx: number, by: number) => {
              playSound("explosion");
              s.screenShake = 14;
              s.hitStop = 0.08;
              s.ball = null;

              const blastRadius = 95;

              // 1. Water splash shockwave & droplet spray particles (nyiprat!)
              for (let k = 0; k < 45; k++) {
                const ang = Math.random() * Math.PI * 2;
                const spd = 60 + Math.random() * 190;
                const colors = ["#00d2ff", "#38b6ff", "#a5f3fc", "#ffffff", "#0077b6", "#67e8f9"];
                s.particles.push({
                  x: bx,
                  y: by,
                  vx: Math.cos(ang) * spd,
                  vy: Math.sin(ang) * spd - 45,
                  life: 0.55 + Math.random() * 0.45,
                  maxLife: 0.85,
                  color: colors[k % colors.length],
                  size: 3.5 + Math.random() * 6.5,
                });
              }

              // 2. Clear / shatter falling branches in splash radius
              s.fallingBranches.forEach((fb) => {
                const dist = Math.hypot(fb.x - bx, fb.y - by);
                if (dist < blastRadius + 40) {
                  fb.wobble = 2.0;
                  for (let k = 0; k < 6; k++) {
                    const ang = Math.random() * Math.PI * 2;
                    const spd = 40 + Math.random() * 80;
                    s.particles.push({
                      x: fb.x,
                      y: fb.y,
                      vx: Math.cos(ang) * spd,
                      vy: Math.sin(ang) * spd,
                      life: 0.4,
                      maxLife: 0.4,
                      color: "#8a5327",
                      size: 4,
                    });
                  }
                }
              });

              // 3. Damage / defeat all monkeys caught in splash radius
              let monkeysHit = 0;
              for (const target of s.monkeys) {
                if (target.hit) continue;
                const p = monkeyPosition(target, s.time);
                const dist = Math.hypot(p.x - bx, p.y - by);
                if (dist <= blastRadius + (target.isBoss ? 45 : 30)) {
                  monkeysHit++;
                  target.helmetBroken = true;
                  target.ironArmorBroken = true;
                  target.hp = 0;
                  target.hit = true;
                  target.hitFlash = 0.5;

                  s.tumblingMonkeys.push({
                    x: p.x,
                    y: p.y,
                    vx: (p.x - bx) * 2.5 + (Math.random() - 0.5) * 60,
                    vy: -220,
                    rot: 0,
                    vRot: (Math.random() - 0.5) * 16,
                    isBoss: target.isBoss,
                    life: 1.6,
                  });

                  s.timeLeft = Math.min(60, s.timeLeft + 10);
                  const newSec = Math.ceil(s.timeLeft);
                  lastSecRef.current = newSec;
                  setTimeLeft(newSec);

                  const pts = target.isBoss ? 500 : 300;
                  setScore((sc) => {
                    const newScore = sc + pts;
                    setHighScore(newScore);
                    setHighScoreState((prev) => Math.max(prev, newScore));
                    return newScore;
                  });
                }
              }

              s.impact = {
                x: bx,
                y: by - 30,
                text: monkeysHit > 1 ? `MULTI-SPLASH x${monkeysHit}! 💦` : "BYUURR! 💦",
                subtext: monkeysHit > 0 ? `+${monkeysHit * 300} (+${monkeysHit * 10}s ⏱️)` : "CIPRATAN AIR!",
                color: "#00f0ff",
                life: 1.2,
              };

              const alive = s.monkeys.filter((m) => !m.hit).length;
              setRemaining(alive);
              if (alive === 0 && s.clearDelay <= 0) {
                s.clearDelay = 1.2;
                setMaxUnlockedLevel(Math.min(20, level + 1));
                setMaxUnlockedLevelState((lvl) => Math.max(lvl, Math.min(20, level + 1)));
              }
            };

            for (let i = 0; i < 3 && s.ball; i++) {
              const step = dt / 3;
              b.trail.push({ x: b.x, y: b.y });
              if (b.trail.length > 14) b.trail.shift();

              b.vx += s.windX * step;
              b.x += b.vx * step;
              b.y += b.vy * step;
              b.vy += (420 + s.windY) * step;

              // Check if Bomb reached destination endpoint at the end of the shot direction ("meledak di ujung arah tembakan")
              if (b.kind === "bomb" && b.targetX !== undefined && b.targetY !== undefined) {
                const distToTarget = Math.hypot(b.x - b.targetX, b.y - b.targetY);
                if (distToTarget < 24 || (b.vy > 0 && b.y >= b.targetY)) {
                  explodeAt(b.targetX, b.targetY);
                  break;
                }
              }

              // Check floor or wall hit for Bomb
              if (b.kind === "bomb" && (b.y >= 705 || b.x < 15 || b.x > W - 15)) {
                explodeAt(Math.max(25, Math.min(W - 25, b.x)), Math.min(b.y, 705));
                break;
              }

              // Agile monkey dodge reaction check (Super shot CANNOT be dodged!)
              for (const m of s.monkeys) {
                if (!m.hit && m.canDodge && m.dodgeTime <= 0 && m.laughTimer <= 0 && b.kind !== "super") {
                  if (m.behavior === "peek" && m.peekPhase === "hidden") continue;
                  const mp = monkeyPosition(m, s.time);
                  const distToPellet = Math.hypot(b.x - mp.x, b.y - mp.y);
                  const pelletSpeed = Math.hypot(b.vx, b.vy);
                  if (distToPellet < 60 && b.bounces === 0 && pelletSpeed < 560 && b.y > mp.y - 45) {
                    m.dodgeTime = 0.45;
                    playSound("dodge");
                    s.particles.push({
                      x: mp.x, y: mp.y - 15,
                      vx: (Math.random() - 0.5) * 35, vy: -20,
                      life: 0.3, maxLife: 0.3, color: "#e8f5e9", size: 3.5,
                    });
                    break;
                  }
                }
              }

              // Tree trunk ricochet / bounce physics
              for (const tree of s.trees) {
                const halfW = tree.width / 2;
                const treeLeft = tree.x - halfW;
                const treeRight = tree.x + halfW;
                if (b.y >= tree.y1 && b.y <= tree.y2) {
                  if (tree.side === "left" && b.x <= treeRight + 6 && b.vx < 0) {
                    if (b.kind === "bomb") {
                      explodeAt(b.x, b.y);
                      break;
                    }
                    b.bounces++;
                    b.vx = -b.vx * 0.65;
                    b.x = treeRight + 8;
                    playSound("wood");
                    s.screenShake = 3;
                    s.impact = { x: b.x + 22, y: b.y, text: "POHON KAYU!", color: "#d7aa6a", life: 0.6 };
                    for (let k = 0; k < 6; k++) {
                      const ang = (Math.random() - 0.5) * Math.PI;
                      const spd = 30 + Math.random() * 50;
                      s.particles.push({
                        x: b.x, y: b.y,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.4, maxLife: 0.4, color: "#5d3a1f", size: 3.5,
                      });
                    }
                  } else if (tree.side === "right" && b.x >= treeLeft - 6 && b.vx > 0) {
                    if (b.kind === "bomb") {
                      explodeAt(b.x, b.y);
                      break;
                    }
                    b.bounces++;
                    b.vx = -b.vx * 0.65;
                    b.x = treeLeft - 8;
                    playSound("wood");
                    s.screenShake = 3;
                    s.impact = { x: b.x - 22, y: b.y, text: "POHON KAYU!", color: "#d7aa6a", life: 0.6 };
                    for (let k = 0; k < 6; k++) {
                      const ang = Math.PI + (Math.random() - 0.5) * Math.PI;
                      const spd = 30 + Math.random() * 50;
                      s.particles.push({
                        x: b.x, y: b.y,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.4, maxLife: 0.4, color: "#5d3a1f", size: 3.5,
                      });
                    }
                  }
                }
              }

              // 1. Falling branch collision & ricochet physics
              for (const fb of s.fallingBranches) {
                const branch = getBranchSegment(fb);
                const dx = branch.x2 - branch.x1;
                const dy = branch.y2 - branch.y1;
                const segLenSq = dx * dx + dy * dy;
                const t = Math.max(0, Math.min(1, ((b.x - branch.x1) * dx + (b.y - branch.y1) * dy) / segLenSq));
                const projX = branch.x1 + t * dx;
                const projY = branch.y1 + t * dy;
                const dist = Math.hypot(b.x - projX, b.y - projY);
                const hitDist = branch.radius + 9;

                if (dist < hitDist) {
                  if (b.kind === "bomb") {
                    explodeAt(b.x, b.y);
                    break;
                  }
                  if (b.kind === "super") {
                    fb.wobble = 2.0;
                    playSound("laser");
                    s.impact = { x: b.x, y: b.y - 14, text: "TEMBUS CABANG! ⚡", color: "#00f0ff", life: 0.6 };
                    for (let k = 0; k < 5; k++) {
                      const ang = Math.random() * Math.PI * 2;
                      const spd = 40 + Math.random() * 60;
                      s.particles.push({
                        x: b.x, y: b.y,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.35, maxLife: 0.35, color: "#00f0ff", size: 3.5,
                      });
                    }
                    continue;
                  }

                  const nx = dist > 0.001 ? (b.x - projX) / dist : 0;
                  const ny = dist > 0.001 ? (b.y - projY) / dist : -1;
                  const dot = b.vx * nx + b.vy * ny;

                  if (dot < 0) {
                    b.bounces++;
                    fb.wobble = 1;
                    if (b.bounces <= 2) {
                      const restitution = 0.68;
                      b.vx = (b.vx - 2 * dot * nx) * restitution;
                      b.vy = (b.vy - 2 * dot * ny) * restitution;
                      b.x = projX + nx * (hitDist + 2);
                      b.y = projY + ny * (hitDist + 2);

                      playSound("wood");
                      s.screenShake = 3;
                      s.impact = { x: b.x, y: b.y - 14, text: "TOK! (PANTUL RANTING)", color: "#ffe082", life: 0.75 };

                      for (let k = 0; k < 7; k++) {
                        const ang = Math.random() * Math.PI * 2;
                        const spd = 40 + Math.random() * 60;
                        s.particles.push({
                          x: b.x,
                          y: b.y,
                          vx: Math.cos(ang) * spd,
                          vy: Math.sin(ang) * spd,
                          life: 0.45,
                          maxLife: 0.45,
                          color: k % 2 ? "#d7aa6a" : "#71b340",
                          size: 3.5,
                        });
                      }
                    } else {
                      s.ball = null;
                      s.streak = 0;
                      setCombo(0);
                      playSound("wood");
                      s.impact = { x: b.x, y: b.y - 12, text: "TOK!", color: "#fff1c3", life: 0.7 };
                      if (s.monkeys.some((m) => !m.hit)) {
                        playSound("laugh");
                        for (const m of s.monkeys) {
                          if (!m.hit) m.laughTimer = 1.9;
                        }
                      }
                      break;
                    }
                  }
                }
              }

              if (!s.ball) break;

              // 2. Unique Interactive Obstacle collision (Crystal Bumper, Beehive, Totem Pillar)
              for (const uo of s.uniqueObstacles) {
                if (uo.broken) continue;

                if (uo.kind === "bumper_crystal") {
                  const dist = Math.hypot(b.x - uo.x, b.y - uo.y);
                  if (dist < uo.radius + 10) {
                    const nx = dist > 0.001 ? (b.x - uo.x) / dist : 0;
                    const ny = dist > 0.001 ? (b.y - uo.y) / dist : -1;
                    const dot = b.vx * nx + b.vy * ny;
                    if (dot < 0) {
                      b.bounces++;
                      uo.hitFlash = 1.0;
                      uo.wobble = 1.6;
                      playSound("bonus");
                      const boost = 1.25;
                      b.vx = (b.vx - 2 * dot * nx) * boost;
                      b.vy = (b.vy - 2 * dot * ny) * boost;
                      s.impact = {
                        x: uo.x,
                        y: uo.y - 18,
                        text: level >= 11 ? "MAGMA BOOST! 🔥 1.25x" : "PANTULAN KRISTAL! 💎 1.25x",
                        color: level >= 11 ? "#ff9f43" : "#00f0ff",
                        life: 0.65,
                      };
                      for (let k = 0; k < 8; k++) {
                        const ang = Math.random() * Math.PI * 2;
                        const spd = 60 + Math.random() * 80;
                        s.particles.push({
                          x: uo.x, y: uo.y,
                          vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                          life: 0.4, maxLife: 0.4,
                          color: level >= 11 ? "#ffdd59" : "#00f0ff", size: 3.5,
                        });
                      }
                      break;
                    }
                  }
                } else if (uo.kind === "beehive") {
                  const dist = Math.hypot(b.x - uo.x, b.y - uo.y);
                  if (dist < uo.radius + 10) {
                    uo.wobble = 2.4;
                    if (b.kind === "bomb" || b.kind === "super" || uo.hp <= 1) {
                      uo.broken = true;
                      uo.hp = 0;
                      setScore((sc) => sc + 200);
                      s.impact = { x: uo.x, y: uo.y - 20, text: "+200 BONUS MADU! 🍯", subtext: "LEBAH MENGAMUK! 🐝", color: "#ffd166", life: 0.9 };
                      playSound("bonus");
                      for (let k = 0; k < 12; k++) {
                        const ang = Math.random() * Math.PI * 2;
                        const spd = 40 + Math.random() * 90;
                        s.particles.push({
                          x: uo.x, y: uo.y,
                          vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                          life: 0.5, maxLife: 0.5,
                          color: k % 2 === 0 ? "#fbbf24" : "#f59e0b", size: 4,
                        });
                      }
                      if (b.kind === "bomb") {
                        explodeAt(b.x, b.y);
                        break;
                      }
                    } else {
                      uo.hp--;
                      uo.hitFlash = 0.8;
                      const nx = dist > 0.001 ? (b.x - uo.x) / dist : 0;
                      const ny = dist > 0.001 ? (b.y - uo.y) / dist : -1;
                      const dot = b.vx * nx + b.vy * ny;
                      if (dot < 0) {
                        b.bounces++;
                        playSound("wood");
                        b.vx = (b.vx - 2 * dot * nx) * 0.6;
                        b.vy = (b.vy - 2 * dot * ny) * 0.6;
                      }
                    }
                    break;
                  }
                } else if (uo.kind === "totem_pillar") {
                  if (Math.abs(b.x - uo.x) < 22 && Math.abs(b.y - uo.y) < 42) {
                    if (b.kind === "super") {
                      uo.hitFlash = 1.0;
                      playSound("laser");
                      s.impact = { x: uo.x, y: uo.y - 24, text: "TEMBUS PILAR! ⚡", color: "#00f0ff", life: 0.6 };
                    } else if (b.kind === "bomb") {
                      uo.broken = true;
                      explodeAt(b.x, b.y);
                      playSound("shatter");
                      s.impact = { x: uo.x, y: uo.y - 24, text: "PILAR KUNO HANCUR! 💥", color: "#ff7675", life: 0.8 };
                      break;
                    } else {
                      b.bounces++;
                      uo.hitFlash = 0.7;
                      uo.wobble = 1.2;
                      playSound("wood");
                      const dx = b.x - uo.x;
                      const dy = b.y - uo.y;
                      if (Math.abs(dx) / 22 > Math.abs(dy) / 42) {
                        b.vx = -b.vx * 0.68;
                      } else {
                        b.vy = -b.vy * 0.68;
                      }
                      break;
                    }
                  }
                }
              }

              if (!s.ball) break;

              // 3. Target monkey collision
              const target = s.monkeys.find((m) => {
                if (m.hit) return false;
                // If completely hidden behind tree trunk, shielded!
                if (m.behavior === "peek" && m.peekPhase === "hidden") return false;
                const p = monkeyPosition(m, s.time);
                const dodgeY = m.dodgeTime > 0 ? Math.sin((m.dodgeTime / 0.45) * Math.PI) * 22 : 0;
                const hitRx = m.isBoss ? 56 : 44;
                const hitRy = m.isBoss ? 66 : 52;
                return Math.hypot((b.x - p.x) / hitRx, (b.y - (p.y + dodgeY - 8)) / hitRy) < 1;
              });

              if (target) {
                if (b.kind === "bomb") {
                  explodeAt(b.x, b.y);
                  break;
                }

                if (b.kind === "super") {
                  if (target.hasHelmet && !target.helmetBroken) {
                    target.helmetBroken = true;
                    for (let k = 0; k < 10; k++) {
                      const ang = Math.random() * Math.PI * 2;
                      const spd = 45 + Math.random() * 65;
                      s.particles.push({
                        x: b.x, y: b.y - 18,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.5, maxLife: 0.5,
                        color: k % 2 === 0 ? "#4a2a12" : "#70401d",
                        size: 4.5,
                      });
                    }
                  }
                  if (target.hasIronArmor && !target.ironArmorBroken) {
                    target.ironArmorBroken = true;
                    for (let k = 0; k < 12; k++) {
                      const ang = Math.random() * Math.PI * 2;
                      const spd = 50 + Math.random() * 80;
                      s.particles.push({
                        x: b.x, y: b.y - 18,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.5, maxLife: 0.5,
                        color: k % 3 === 0 ? "#cbd5e0" : k % 3 === 1 ? "#ffba08" : "#718096",
                        size: 4,
                      });
                    }
                  }
                  s.hitStop = 0.08;
                  s.screenShake = 9;
                  target.hp -= 2;
                  playSound("laser");
                } else {
                  // Check if monkey has iron armor intact (absorbs 1 hit)
                  if (target.hasIronArmor && !target.ironArmorBroken) {
                    target.ironArmorBroken = true;
                    target.hitFlash = 0.4;
                    s.screenShake = 7;
                    s.hitStop = 0.06;
                    playSound("ricochet");

                    // Spawn metallic silver & orange sparks
                    for (let k = 0; k < 12; k++) {
                      const ang = Math.random() * Math.PI * 2;
                      const spd = 50 + Math.random() * 80;
                      s.particles.push({
                        x: b.x, y: b.y - 18,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.5, maxLife: 0.5,
                        color: k % 3 === 0 ? "#cbd5e0" : k % 3 === 1 ? "#ffba08" : "#718096",
                        size: 4,
                      });
                    }

                    s.impact = { x: b.x, y: b.y - 20, text: "ZIRAH PECAH! 🛡️", color: "#e2e8f0", life: 0.85 };
                    b.bounces++;
                    b.vx = -b.vx * 0.55;
                    b.vy = -b.vy * 0.55;
                    break;
                  }

                  // Check if monkey has coconut helmet intact
                  if (target.hasHelmet && !target.helmetBroken) {
                    target.helmetBroken = true;
                    target.hitFlash = 0.4;
                    s.screenShake = 6;
                    s.hitStop = 0.06;
                    playSound("shatter");

                    // Spawn coconut shell shards
                    for (let k = 0; k < 10; k++) {
                      const ang = Math.random() * Math.PI * 2;
                      const spd = 45 + Math.random() * 65;
                      s.particles.push({
                        x: b.x, y: b.y - 18,
                        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
                        life: 0.5, maxLife: 0.5,
                        color: k % 2 === 0 ? "#4a2a12" : "#70401d",
                        size: 4.5,
                      });
                    }

                    s.impact = { x: b.x, y: b.y - 20, text: "HELM PECAH! 🥥", color: "#f39c12", life: 0.85 };
                    b.bounces++;
                    b.vx = -b.vx * 0.45;
                    b.vy = -b.vy * 0.45;
                    break;
                  }

                  s.hitStop = 0.05;
                  s.screenShake = target.isBoss ? 7.5 : 5;
                  target.hp--;
                }

                target.hitFlash = 0.35;

                if (target.hp <= 0) {
                  target.hit = true;
                  const curP = monkeyPosition(target, s.time);
                  s.tumblingMonkeys.push({
                    x: curP.x,
                    y: curP.y,
                    vx: b.vx * 0.22,
                    vy: -170,
                    rot: 0,
                    vRot: (Math.random() - 0.5) * 14,
                    isBoss: target.isBoss,
                    life: 1.5,
                  });

                  playSound(target.isBoss ? "bossDefeat" : "hit");
                  s.streak++;

                  let bonus = 0;
                  let impactText = b.kind === "super" ? "SUPER KILL! ⚡" : "KENAA!";
                  let impactColor = b.kind === "super" ? "#00f0ff" : "#ffe37a";
                  let awardBonusAmmo = false;

                  const isTrickShot = b.bounces > 0;
                  const isCilukba = target.behavior === "peek" && (target.peekPhase === "exposed" || target.peekPhase === "peeking");
                  const isPatrol = target.behavior === "patrol";

                  if (isTrickShot) {
                    impactText = `TRICK SHOT! x${s.streak}`;
                    bonus = 300;
                    impactColor = "#ffea52";
                    awardBonusAmmo = true;
                  } else if (target.isBoss) {
                    impactText = "BOS KALAH!";
                    bonus = 400;
                    impactColor = "#ff4d6d";
                  } else if (isCilukba) {
                    impactText = `CILUKBA! x${s.streak}`;
                    bonus = 250;
                    impactColor = "#00f0ff";
                    awardBonusAmmo = true;
                  } else if (isPatrol) {
                    impactText = `SNIPER JALAN! x${s.streak}`;
                    bonus = 200;
                    impactColor = "#76d275";
                  } else if (s.streak > 1) {
                    impactText = `KOMBO x${s.streak}!`;
                  }

                  // Kill reward: +10 Seconds!
                  s.timeLeft = Math.min(60, s.timeLeft + 10);
                  const newSec = Math.ceil(s.timeLeft);
                  lastSecRef.current = newSec;
                  setTimeLeft(newSec);

                  if (awardBonusAmmo) {
                    s.shots = Math.min(12, s.shots + 1);
                    setShots(s.shots);
                    playSound("bonus");
                  }

                  const basePoints = (target.isBoss ? 500 : 100) + bonus;
                  const points = Math.round(basePoints * s.streak * (isTrickShot ? 2.2 : 1));
                  setCombo(s.streak);
                  setScore((prev) => {
                    const newScore = prev + points;
                    setHighScore(newScore);
                    setHighScoreState((p) => Math.max(p, newScore));
                    return newScore;
                  });

                  const subtextParts = [`+${points}`, "+10s ⏱️"];
                  if (awardBonusAmmo) subtextParts.push("(+1 PELURU! 🎯)");

                  s.impact = {
                    x: Math.max(90, Math.min(W - 90, b.x)),
                    y: b.y - 35,
                    text: impactText,
                    subtext: subtextParts.join(" "),
                    color: impactColor,
                    life: 1.3,
                  };

                  for (let j = 0; j < 22; j++) {
                    const ang = (j * Math.PI * 2) / 22;
                    const pSpeed = 75 + (j % 4) * 30;
                    s.particles.push({
                      x: b.x,
                      y: b.y,
                      vx: Math.cos(ang) * pSpeed,
                      vy: Math.sin(ang) * pSpeed - 20,
                      life: 0.45,
                      maxLife: 0.45,
                      color: b.kind === "super" ? (j % 2 ? "#00f0ff" : "#ffffff") : (j % 2 ? "#ffd700" : "#ff8000"),
                      size: 4,
                    });
                  }

                  const alive = s.monkeys.filter((m) => !m.hit).length;
                  setRemaining(alive);
                  if (alive === 0) {
                    s.clearDelay = 1.0;
                    setMaxUnlockedLevel(Math.min(20, level + 1));
                    setMaxUnlockedLevelState((lvl) => Math.max(lvl, Math.min(20, level + 1)));
                  }
                } else {
                  playSound("bossHit");
                  s.timeLeft = Math.min(60, s.timeLeft + 3);
                  const newSec = Math.ceil(s.timeLeft);
                  lastSecRef.current = newSec;
                  setTimeLeft(newSec);

                  s.impact = {
                    x: b.x,
                    y: b.y - 18,
                    text: b.kind === "super" ? `SUPER HIT! SISA HP: ${target.hp} 💥` : `KUMPULAN BOS! SISA HP: ${target.hp}`,
                    subtext: "+3s ⏱️",
                    color: "#ff8400",
                    life: 0.9,
                  };
                  b.bounces++;
                  b.vx = -b.vx * 0.4;
                  b.vy = -b.vy * 0.4;

                  for (let j = 0; j < 10; j++) {
                    const ang = (j * Math.PI * 2) / 10;
                    s.particles.push({
                      x: b.x,
                      y: b.y,
                      vx: Math.cos(ang) * 55,
                      vy: Math.sin(ang) * 55,
                      life: 0.35,
                      maxLife: 0.35,
                      color: "#ff8400",
                      size: 4,
                    });
                  }
                }

                s.ball = null;
                break;
              }

              // 3. Screen bounds check
              if (b.x < -20 || b.x > W + 20 || b.y < -40 || b.y > H + 20) {
                s.ball = null;
                s.streak = 0;
                setCombo(0);
                playSound("miss");
                s.impact = {
                  x: Math.max(85, Math.min(W - 85, b.x)),
                  y: Math.max(90, Math.min(H - 120, b.y)),
                  text: "LEPAS!",
                  color: "#fff0b4",
                  life: 0.7,
                };
                if (s.monkeys.some((m) => !m.hit)) {
                  playSound("laugh");
                  for (const m of s.monkeys) {
                    if (!m.hit) m.laughTimer = 1.9;
                  }
                }
                break;
              }
            }
          }

          // Update tumbling monkeys
          for (const tm of s.tumblingMonkeys) {
            tm.x += tm.vx * dt;
            tm.y += tm.vy * dt;
            tm.vy += 480 * dt;
            tm.rot += tm.vRot * dt;
            tm.life -= dt * 0.9;
          }
          s.tumblingMonkeys = s.tumblingMonkeys.filter(tm => tm.life > 0);

          // Update particles
          for (const p of s.particles) {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vy += 150 * dt;
            p.life -= dt * 1.5;
          }
          s.particles = s.particles.filter(p => p.life > 0);

          // Update impact text
          if (s.impact) {
            s.impact.life -= dt * 1.2;
            if (s.impact.life <= 0) s.impact = null;
          }

          // Level transition
          if (s.clearDelay > 0) {
            s.clearDelay -= dt;
            if (s.clearDelay <= 0) {
              const currentStars = s.shots >= 3 ? 3 : s.shots >= 1 ? 2 : 1;
              setStars(currentStars);
              playSound(level === 20 || level === 10 ? "bossDefeat" : "clear");
              setMode(level >= 20 ? "won" : "cleared");
            }
          }

          if (!s.ball && s.shots === 0 && s.monkeys.some(m => !m.hit) && s.clearDelay <= 0 && s.timeLeft > 0) {
            playSound("lost");
            setLossReason("ammo");
            setMode("lost");
          }
        }
      }

      draw(ctx, s, level, art.current);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [level, playSound]);

  const reset = useCallback((next: number, freshRun = false) => {
    scene.current = makeScene(next, activeSkin);
    setLevel(next);
    setShots(scene.current.shots);
    setRemaining(scene.current.monkeys.length);
    setTimeLeft(Math.ceil(scene.current.timeLeft));
    lastSecRef.current = Math.ceil(scene.current.timeLeft);
    setLossReason(null);
    setCombo(0);
    setActiveSkill(null);
    setMode("playing");
    if (freshRun) setScore(0);
  }, [activeSkin]);

  useEffect(() => {
    if (mode !== "cleared") return;
    const timer = window.setTimeout(() => reset(level + 1), 1300);
    return () => window.clearTimeout(timer);
  }, [mode, level, reset]);

  function point(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * W) / rect.width,
      y: ((event.clientY - rect.top) * H) / rect.height,
    };
  }

  function aim(event: React.PointerEvent<HTMLCanvasElement>) {
    if (modeRef.current !== "playing" || scene.current.ball || scene.current.clearDelay > 0 || scene.current.shots <= 0) return;
    const start = point(event);
    const sling = scene.current.sling;
    if (start.y < sling.y - 65 || start.y > sling.y + 135 || Math.abs(start.x - sling.x) > 105) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = start;
    dragOffset.current = { x: start.x - sling.x, y: start.y - sling.y };
    scene.current.aim = { ...sling };
  }

  function updatePull(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragStart.current) return;
    const sling = scene.current.sling;
    const p = point(event);
    const rawDx = (p.x - dragOffset.current.x) - sling.x;
    const rawDy = Math.max(0, (p.y - dragOffset.current.y) - sling.y);
    const pullLen = Math.hypot(rawDx, rawDy);
    const factor = pullLen > MAX_PULL ? MAX_PULL / pullLen : 1;
    scene.current.aim = { x: sling.x + rawDx * factor, y: sling.y + rawDy * factor };
  }

  function release(event: React.PointerEvent<HTMLCanvasElement>) {
    const s = scene.current;
    if (!dragStart.current || modeRef.current !== "playing" || s.ball) return;
    updatePull(event);
    const sling = s.sling;
    const dx = sling.x - (s.aim?.x ?? sling.x);
    const dy = sling.y - (s.aim?.y ?? sling.y);
    dragStart.current = null;
    const currentAim = s.aim;
    s.aim = null;

    const length = Math.hypot(dx, dy);
    if (dy > -14 || length < 18) return;

    const chosenKind: ProjectileKind = s.activeSkill || "normal";
    const speedMult = chosenKind === "super" ? 1.35 : 1.0;
    const speed = (430 + Math.min(length, MAX_PULL) * 2.6) * speedMult;

    // Calculate predicted target endpoint for Bomb (meledak di ujung arah tembakan!)
    let targetX: number | undefined;
    let targetY: number | undefined;
    if (chosenKind === "bomb" && currentAim) {
      const vx = (dx / length) * speed;
      const vy = (dy / length) * speed;
      let simX = sling.x;
      let simY = sling.y;
      let simVx = vx;
      let simVy = vy;
      const simDt = 0.035;
      let endX = simX;
      let endY = simY;
      for (let step = 0; step < 28; step++) {
        simVx += s.windX * simDt;
        simX += simVx * simDt;
        simY += simVy * simDt;
        simVy += (420 + s.windY) * simDt;
        endX = simX;
        endY = simY;
        let hit = false;
        for (const fb of s.fallingBranches) {
          const seg = getBranchSegment(fb);
          if (segmentDistance(simX, simY, seg.x1, seg.y1, seg.x2, seg.y2) < seg.radius + 8) {
            hit = true;
            break;
          }
        }
        for (const uo of s.uniqueObstacles) {
          if (!uo.broken && uo.kind === "totem_pillar" && Math.abs(simX - uo.x) < 20 && Math.abs(simY - uo.y) < 42) {
            hit = true;
            break;
          }
        }
        if (hit || simX < 0 || simX > W || simY < 0 || simY > H) break;
      }
      targetX = endX;
      targetY = endY;
    }

    s.ball = {
      x: sling.x,
      y: sling.y,
      vx: (dx / length) * speed,
      vy: (dy / length) * speed,
      trail: [],
      bounces: 0,
      kind: chosenKind,
      targetX,
      targetY,
    };

    if (s.activeSkill) {
      if (s.activeSkill === "super") {
        setAmmoSuper((prev) => {
          const next = Math.max(0, prev - 1);
          setStoredSkillAmmo("super", next);
          return next;
        });
      } else {
        setAmmoBomb((prev) => {
          const next = Math.max(0, prev - 1);
          setStoredSkillAmmo("bomb", next);
          return next;
        });
      }
      s.activeSkill = null;
      setActiveSkill(null);
    }

    s.shots--;
    s.recoil = 1;
    s.screenShake = chosenKind === "super" ? 5 : chosenKind === "bomb" ? 4 : 2.5;
    setShots(s.shots);
    if (chosenKind === "super") {
      playSound("laser");
    } else if (chosenKind === "bomb") {
      playSound("bomb");
    } else {
      playSound("shoot");
    }
  }

  const handleSelectSkill = (type: SkillType) => {
    const ammo = type === "super" ? ammoSuper : ammoBomb;
    if (ammo <= 0) {
      setAdRewardTarget(type);
      setActiveModal("ad");
      return;
    }
    if (activeSkill === type) {
      setActiveSkill(null);
      scene.current.activeSkill = null;
    } else {
      setActiveSkill(type);
      scene.current.activeSkill = type;
    }
  };

  const handleEquipSkin = (skinId: SlingshotSkinId) => {
    setActiveSkin(skinId);
    setActiveSkinState(skinId);
    scene.current.skinId = skinId;
  };

  const handleWatchAd = async () => {
    if (isWatchingAd) return;
    setIsWatchingAd(true);
    setAdCountdown(3);

    const success = await showTikTokRewardedVideoAd({
      rewardType: typeof adRewardTarget === "string" ? adRewardTarget : "skin",
      skinId: typeof adRewardTarget === "object" ? adRewardTarget.skinId : undefined,
      onProgress: (sec) => setAdCountdown(sec),
    });

    setIsWatchingAd(false);

    if (success) {
      playSound("bonus");
      if (typeof adRewardTarget === "string") {
        if (adRewardTarget === "super") {
          setAmmoSuper((prev) => {
            const next = prev + 1;
            setStoredSkillAmmo("super", next);
            return next;
          });
        } else {
          setAmmoBomb((prev) => {
            const next = prev + 1;
            setStoredSkillAmmo("bomb", next);
            return next;
          });
        }
      } else {
        setSkinUnlocked(adRewardTarget.skinId);
        handleEquipSkin(adRewardTarget.skinId);
      }
      setActiveModal(null);
    }
  };

  const isBossLevel = level === 10 || level === 20;

  return (
    <main className="monkey-page">
      <div className="monkey-shell">
        <h1 className="monkey-visually-hidden">Tembak Monyetnya!</h1>
        <section className="monkey-cabinet" aria-label="Permainan Tembak Monyetnya">
          <div className={`monkey-stage ${level >= 11 ? "stage-volcano" : ""}`}>
            <canvas
              ref={canvas}
              width={W}
              height={H}
              onPointerDown={aim}
              onPointerMove={updatePull}
              onPointerUp={release}
              onPointerCancel={() => {
                dragStart.current = null;
                scene.current.aim = null;
              }}
              aria-label="Tarik kantong ketapel ke bawah dan belakang, lalu lepaskan untuk menembak ke arah berlawanan"
            />

            {mode !== "menu" && (
              <button
                type="button"
                className="monkey-nav-btn"
                onClick={() => setMode("menu")}
                title="Kembali ke Menu Utama"
                aria-label="Kembali ke Menu Utama"
              >
                <Home size={18} />
              </button>
            )}

            {mode !== "menu" && (
              <div className="monkey-hud" aria-label="Status permainan">
                <span className="monkey-stat" aria-label={`Babak ${level} dari 20`}>
                  <small>BABAK</small>
                  <b>
                    {String(level).padStart(2, "0")}
                    <span className="monkey-stat-muted">/20</span>
                    {isBossLevel && <span className="monkey-boss-pill">{level === 20 ? "KAISAR" : "BOS"}</span>}
                  </b>
                </span>
                <span className="monkey-stat" aria-label={`${timeLeft} detik tersisa`}>
                  <small>WAKTU</small>
                  <b className={timeLeft <= 5 ? "monkey-time-low" : ""}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {timeLeft}s
                  </b>
                </span>
                <span className="monkey-stat" aria-label={`${remaining} monyet tersisa`}>
                  <small>{isBossLevel ? "TARGET" : "MONYET"}</small>
                  <b>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="7" />
                      <path d="M5 9a3 3 0 1 0-1 5m15-5a3 3 0 1 1 1 5" />
                      <path d="M9 11h.01M15 11h.01M9 15q3 2 6 0" />
                    </svg>
                    {remaining}
                  </b>
                </span>
                <span className="monkey-stat" aria-label={`${shots} peluru tersisa`}>
                  <small>PELURU</small>
                  <b>
                    <span className="monkey-pellet-icon" aria-hidden="true" />
                    {shots}
                  </b>
                </span>
                <span className="monkey-stat" aria-label={`Skor ${score}${combo > 1 ? `, kombo ${combo}` : ""}`}>
                  <small>SKOR</small>
                  <b>
                    <span className="monkey-stat-icon" aria-hidden="true">✦</span>
                    {score}
                  </b>
                </span>
              </div>
            )}

            <button
              type="button"
              className="monkey-music-btn"
              onClick={() => setSoundOn((s) => !s)}
              title={soundOn ? "Matikan Musik & Suara" : "Nyalakan Musik & Suara"}
              aria-label={soundOn ? "Matikan Musik & Suara" : "Nyalakan Musik & Suara"}
            >
              {soundOn ? "🔊" : "🔇"}
            </button>

            {mode === "playing" && (
              <div className="monkey-skills-dock" aria-label="Dock Jurus Spesial">
                <button
                  type="button"
                  className={`monkey-skill-btn skill-super ${activeSkill === "super" ? "active" : ""}`}
                  onClick={() => handleSelectSkill("super")}
                  title="Tembakan Super ⚡ (Tembus Ranting & Pecah Zirah)"
                  aria-label="Tembakan Super"
                >
                  <Zap size={16} color="#00f0ff" />
                  <span>SUPER</span>
                  <span className="monkey-skill-badge">{ammoSuper}</span>
                </button>
                <button
                  type="button"
                  className={`monkey-skill-btn skill-bomb ${activeSkill === "bomb" ? "active" : ""}`}
                  onClick={() => handleSelectSkill("bomb")}
                  title="Balon Air 💦 (Cipratan Area Luas)"
                  aria-label="Balon Air"
                >
                  <Droplets size={16} color="#00f0ff" />
                  <span>BALON AIR</span>
                  <span className="monkey-skill-badge">{ammoBomb}</span>
                </button>
              </div>
            )}

            {mode === "playing" && level === 1 && shots === 5 && (
              <span className="monkey-hint">TARIK ↓ · BIDIK PARABOLA · LEPAS</span>
            )}
            <span className="monkey-watermark">STERIA HOUSE</span>

            {/* TikTok Main Menu Screen */}
            {mode === "menu" && (
              <div className="monkey-menu-screen">
                <div className="monkey-menu-header">
                  <div className="monkey-tiktok-pill">
                    <span>🔥 TIKTOK MINI GAME</span>
                  </div>
                  <h2 className="monkey-menu-title">TEMBAK MONYETNYA! 🐒</h2>
                  <p className="monkey-menu-tagline">Bidik, pantulkan ke ranting, dan kalahkan Raja Monyet!</p>
                  <div className="monkey-highscore-box">
                    <div className="monkey-highscore-item">
                      <small>Skor Terbaik</small>
                      <b>
                        <Trophy size={16} color="#ffd166" />
                        {highScore}
                      </b>
                    </div>
                    <div className="monkey-highscore-divider" />
                    <div className="monkey-highscore-item">
                      <small>Babak Terbuka</small>
                      <b>{maxUnlockedLevel} / 20</b>
                    </div>
                  </div>
                </div>

                <div className="monkey-menu-center">
                  <button
                    type="button"
                    className="monkey-btn-main-play"
                    onClick={() => reset(maxUnlockedLevel, false)}
                  >
                    <Play size={22} fill="currentColor" />
                    <span>MAIN SEKARANG</span>
                  </button>

                  <div className="monkey-menu-actions">
                    <button
                      type="button"
                      className="monkey-btn-sub"
                      onClick={() => setActiveModal("levels")}
                    >
                      <span>🎯 BABAK</span>
                    </button>
                    <button
                      type="button"
                      className="monkey-btn-sub"
                      onClick={() => setActiveModal("skins")}
                    >
                      <Palette size={16} />
                      <span>SKIN</span>
                    </button>
                  </div>
                </div>

                <div className="monkey-menu-footer">
                  <p className="monkey-menu-tip">💡 Tip: Tarik ketapel ke belakang, gunakan Tembakan Super untuk menembus ranting!</p>
                  <span className="monkey-watermark" style={{ position: "static", opacity: 0.7 }}>STERIA HOUSE</span>
                </div>
              </div>
            )}

            {/* In-Game Result Overlays (Won, Lost, Cleared) */}
            {mode !== "playing" && mode !== "menu" && (
              <div className="monkey-overlay">
                <div className="monkey-card">
                  <div className="monkey-card-header">
                    <span className="monkey-card-label">
                      {mode === "lost"
                        ? lossReason === "time" ? "WAKTU HABIS" : "PELURU HABIS"
                        : mode === "won" ? "TAMAT: JUARA 20 BABAK!" : `BABAK ${level} BERES`}
                    </span>
                  </div>
                  <h2>
                    {mode === "lost"
                      ? lossReason === "time" ? "Waktu Habis! ⏰" : "Peluru Habis!"
                      : mode === "won" ? "Kaisar Monyet Purba Menyerah! 👑🌋" : "Kena Semua! 🎉"}
                  </h2>
                  {mode !== "lost" && (
                    <div className="monkey-stars" aria-label={`${stars} bintang`}>
                      {Array.from({ length: 3 }).map((_, i) => (
                        <span key={i} className={`monkey-star ${i < stars ? "filled" : ""}`}>
                          ★
                        </span>
                      ))}
                    </div>
                  )}
                  <p>
                    {mode === "lost"
                      ? lossReason === "time"
                        ? "Waktu membidik habis! Setiap monyet yang kamu jatuhkan memberi bonus +10 detik."
                        : "Gunakan pantulan ranting (ricochet) dan ikuti titik parabola untuk akurasi terbaik."
                      : mode === "won"
                      ? `Luar biasa! Seluruh 20 babak tuntas dengan skor ${score}! Kamu adalah Legenda Rimba dan Penakluk Kawah Magma!`
                      : "Babak berikutnya dimulai otomatis..."}
                  </p>
                  {mode !== "cleared" && (
                    <>
                      <button
                        type="button"
                        className="monkey-btn-retry"
                        onClick={() => reset(mode === "won" ? 1 : level, true)}
                      >
                        <RotateCcw className="monkey-btn-icon" size={18} strokeWidth={2.6} />
                        <span>{mode === "won" ? "MAIN LAGI DARI AWAL" : "ULANGI BABAK"}</span>
                      </button>
                      <button
                        type="button"
                        className="monkey-btn-retry"
                        style={{ marginTop: 8, background: "#4a3520", color: "#f7dfb8", boxShadow: "0 4px 0 #281c10" }}
                        onClick={() => setMode("menu")}
                      >
                        <Home size={16} />
                        <span>MENU UTAMA</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Skins Wardrobe Modal */}
            {activeModal === "skins" && (
              <div className="monkey-modal-overlay">
                <div className="monkey-modal-card">
                  <button
                    type="button"
                    className="monkey-modal-close"
                    onClick={() => setActiveModal(null)}
                    title="Tutup"
                    aria-label="Tutup"
                  >
                    <X size={18} />
                  </button>
                  <h3 className="monkey-modal-title">Lemari Skin Ketapel</h3>
                  <p className="monkey-modal-desc">Pilih skin ketapel favoritmu untuk efek karet, bingkai, dan jejak tembakan khusus!</p>
                  <div className="monkey-skins-grid">
                    {SLINGSHOT_SKINS.map((sk) => {
                      const unlocked = isSkinUnlocked(sk.id, maxUnlockedLevel);
                      const isEquipped = activeSkin === sk.id;
                      return (
                        <div
                          key={sk.id}
                          className={`monkey-skin-card ${isEquipped ? "active-skin" : ""}`}
                        >
                          <div className="monkey-skin-icon">{sk.icon}</div>
                          <div className="monkey-skin-info">
                            <h4>{sk.name}</h4>
                            <p>{sk.tagline}</p>
                          </div>
                          {isEquipped ? (
                            <button type="button" className="monkey-skin-btn btn-equipped" disabled>
                              DIPAKAI
                            </button>
                          ) : unlocked ? (
                            <button
                              type="button"
                              className="monkey-skin-btn btn-equip"
                              onClick={() => handleEquipSkin(sk.id)}
                            >
                              PAKAI
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="monkey-skin-btn btn-unlock-ad"
                              onClick={() => {
                                setAdRewardTarget({ skinId: sk.id });
                                setActiveModal("ad");
                              }}
                              title={`Buka di Babak ${sk.unlockLevel} atau tonton 1 iklan`}
                            >
                              📺 BUKA (AD)
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Level Selector Modal */}
            {activeModal === "levels" && (
              <div className="monkey-modal-overlay">
                <div className="monkey-modal-card">
                  <button
                    type="button"
                    className="monkey-modal-close"
                    onClick={() => setActiveModal(null)}
                    title="Tutup"
                    aria-label="Tutup"
                  >
                    <X size={18} />
                  </button>
                  <h3 className="monkey-modal-title">Pilih Babak</h3>
                  <p className="monkey-modal-desc">Mainkan kembali babak yang telah berhasil kamu selesaikan.</p>
                  <div className="monkey-levels-grid">
                    {Array.from({ length: 20 }).map((_, idx) => {
                      const lvlNum = idx + 1;
                      const isUnlocked = lvlNum <= maxUnlockedLevel;
                      const isCurrent = lvlNum === level;
                      return (
                        <button
                          key={lvlNum}
                          type="button"
                          className={`monkey-level-tile ${isUnlocked ? (isCurrent ? "active-level" : "") : "locked"}`}
                          disabled={!isUnlocked}
                          onClick={() => {
                            setActiveModal(null);
                            reset(lvlNum, true);
                          }}
                        >
                          <span>{lvlNum}</span>
                          {lvlNum === 10 && <span style={{ fontSize: 9 }}>👑</span>}
                          {lvlNum === 20 && <span style={{ fontSize: 8 }}>👑🔥</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Rewarded Video Ad Modal */}
            {activeModal === "ad" && (
              <div className="monkey-modal-overlay">
                <div className="monkey-modal-card">
                  <button
                    type="button"
                    className="monkey-modal-close"
                    onClick={() => !isWatchingAd && setActiveModal(null)}
                    title="Tutup"
                    aria-label="Tutup"
                    disabled={isWatchingAd}
                  >
                    <X size={18} />
                  </button>
                  <h3 className="monkey-modal-title">Iklan Berhadiah TikTok</h3>
                  <p className="monkey-modal-desc">
                    {isWatchingAd
                      ? "Sedang memutar iklan sponsor TikTok... Mohon tunggu."
                      : "Tonton video singkat (3 detik) untuk mendapatkan hadiah!"}
                  </p>
                  <div className="monkey-ad-box">
                    <div className="monkey-ad-tv-icon">{isWatchingAd ? "📺 ✨" : "🎁"}</div>
                    {isWatchingAd ? (
                      <>
                        <div className="monkey-ad-progress-bar">
                          <div
                            className="monkey-ad-progress-fill"
                            style={{ width: `${((3 - adCountdown) / 3) * 100}%` }}
                          />
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 900, color: "#2d6a4f" }}>
                          Menonton iklan... {adCountdown}s
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="monkey-ad-reward-select">
                          <div
                            className={`monkey-ad-reward-option ${adRewardTarget === "super" ? "selected" : ""}`}
                            onClick={() => setAdRewardTarget("super")}
                          >
                            ⚡ +1 SUPER SHOT
                          </div>
                          <div
                            className={`monkey-ad-reward-option ${adRewardTarget === "bomb" ? "selected" : ""}`}
                            onClick={() => setAdRewardTarget("bomb")}
                          >
                            💦 +1 BALON AIR
                          </div>
                        </div>
                        {typeof adRewardTarget === "object" && (
                          <p style={{ fontSize: 11, color: "#d90429", fontWeight: 800 }}>
                            Hadiah: Membuka Skin {SLINGSHOT_SKINS.find((s) => s.id === (adRewardTarget as { skinId: SlingshotSkinId }).skinId)?.name}
                          </p>
                        )}
                        <button
                          type="button"
                          className="monkey-btn-retry"
                          style={{ background: "linear-gradient(90deg, #ff0050 0%, #00f2fe 100%)", color: "#fff", border: "none" }}
                          onClick={handleWatchAd}
                        >
                          <Play size={16} fill="currentColor" />
                          <span>TONTON SEKARANG (3s)</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
